/**
 * PROTECTED FILE — DO NOT MODIFY WITHOUT PERMISSION
 *
 * Last verified working: 2026-01-03
 *
 * This file controls: core game tick and time progression
 * Changes here have historically broken core game function.
 *
 * Before modifying:
 * 1. Get explicit approval from Zach
 * 2. Create a checkpoint commit
 * 3. Test full baseline after ANY change
 */

import React, { useEffect, useRef } from "react";
import { debug, debugStyled } from "../../lib/debug.js";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { AIRPORTS, calculateDistance } from "../../data/airports.js";
import { AIRPORTS as FULL_AIRPORTS } from "../../systems/AirportDatabase.js";
import { GLOBAL_AIRPORTS } from "../../data/GlobalAirports.js";
import { AIRCRAFT_DATABASE } from "../../data/aircraft.js";
import {
  createEOSState as createEOSStateFromSystem,
  processEOSDaily,
  processA380Purchase,
  recordCancellation,
  recordDiversion,
  getEOSStatus,
  EOS_REQUIREMENTS,
  ROLL_PROBABILITIES,
  RARE_AIRCRAFT,
} from "../../systems/EOSSystem.js";
import {
  TIER_THRESHOLDS,
  REPUTATION_POINTS,
  recordReputationEvent,
  recordFlightCompletion,
  getTierInfo,
  calculateTier,
  createReputationState,
} from "../../systems/ReputationEngine.js";
import {
  createRelationsState,
  airportShiftsLoyalty,
  recordAirportRelationsEvent,
  RELATIONS_THRESHOLDS,
} from "../../systems/RelationsEngine.js";
import { depleteRoute } from "../../systems/RouteDemandSystem.js";
import {
  getCrewBustPreventionChance,
  getRevenueBoost,
  processMonthlyTechCosts,
} from "../../systems/TechSystems.js";
import {
  getNegotiationBonus,
  processMonthlyConsultantCosts,
} from "../../systems/ConsultantsSystem.js";
import {
  getMaintenanceCostModifier,
  getMechanicEfficiency,
  getTurnaroundTimeModifier,
} from "../../systems/SupportCrewSystem.js";
import { processMonthlyCrewCosts } from "../../systems/CrewMarketSystem.js";
import { processMonthlyAdvertising } from "../../systems/AdvertisingSystem.js";
import { tickFuelPriceDaily, processMonthlyFuel, getEffectiveFuelPrice, recordFuelUsage, processDailyFuelSimple } from "../../systems/FuelSystem.js";
import { processSlotWarnings, SLOT_STATUS, calculateDelayPenalties, calculateSlotDelay } from "../../systems/SlotSchedulingSystem.js";
import { intermediatePoint } from "./GreatCircleNav.jsx";
import { soundManager, SOUNDS } from "../../systems/SoundManager";
import {
  calculateFlightSatisfaction,
  getCrewSkillLevel,
} from "../../systems/PassengerSatisfactionSystem.js";
import {
  shouldDivert,
  findAlternateAirport,
  initiateDiversion,
  canSafelyDivert,
} from "../../systems/DiversionSystem.js";
import {
  checkAutoCancellation,
  processAutoCancellation,
} from "../../systems/AutoCancellationSystem.js";
import { updateZombieNews } from "../anomalous/ZombieNewsSystem.js";
import { processMilestones } from "../../systems/WingsSystem.js";

// Hub base values for auction eligibility checks
// Player must have cash >= hub base value to receive invite
const HUB_BASE_VALUES = {
  // Mega Hubs
  KATL: 65000000, KJFK: 70000000, KLAX: 68000000, KORD: 62000000, KDFW: 60000000,
  // Major Hubs
  KDEN: 50000000, KSFO: 55000000, KSEA: 48000000, KMIA: 52000000, KPHX: 45000000,
  KBOS: 50000000, KEWR: 55000000, KMSP: 42000000, KDTW: 40000000, KCLT: 45000000,
  // Regional Hubs
  KSLC: 35000000, KLAS: 38000000, KSAN: 32000000, KPDX: 30000000, KBNA: 28000000,
  KAUS: 30000000, KRDU: 26000000, KCMH: 24000000, KPIT: 25000000, KSTL: 26000000,
};
const DEFAULT_HUB_VALUE = 25000000;

// Get hub base value
function getHubBaseValue(hubCode) {
  return HUB_BASE_VALUES[hubCode] || DEFAULT_HUB_VALUE;
}

/**
 * Get normalized airport coordinates from AIRPORTS, FULL_AIRPORTS, or GLOBAL_AIRPORTS
 * All use object format: { lat, lon, ... }
 */
function getAirportData(code) {
  // Check curated airports first (~170 airports)
  const airport = AIRPORTS[code];
  if (airport && typeof airport.lat === 'number') {
    return airport;
  }

  // Check full database (~400 airports)
  const fullAirport = FULL_AIRPORTS[code];
  if (fullAirport && typeof fullAirport.lat === 'number') {
    return fullAirport;
  }

  // Check global database (~4800 airports) as final fallback
  const globalAirport = GLOBAL_AIRPORTS[code];
  if (globalAirport && typeof globalAirport.lat === 'number') {
    return globalAirport;
  }

  // Log failure only if code looks valid
  if (code && code !== 'undefined') {
    console.warn(`[GameLoopEngine] getAirportData FAILED for "${code}"`);
  }

  return null;
}
// TierProgressionSystem REMOVED - tier is now computed from reputation score
// Tier = calculateTier(reputationScore) - single source of truth
// See ReputationEngine.js for TIER_THRESHOLDS
import { 
  getNextNegotiationAirport,
  createEndOfDayState,
} from "../../systems/EndOfDaySystem.js";

// Re-export for other components
export { AIRPORTS, calculateDistance };

// ============================================================================
// SIMULATED WEATHER ENGINE - DEPRECATED
// ============================================================================
// The simulated weather system has been removed in favor of live real-time
// weather from Tomorrow.io and RainViewer. Weather data is now fetched on
// real-world time intervals, not game time, following MSFS 2024 philosophy:
// "Weather is the current real-world snapshot - time compression moves flights
// through that weather faster, but doesn't accelerate the weather itself."
//
// Live weather is handled by:
// - src/services/WeatherService.js (Tomorrow.io API)
// - src/systems/WeatherImpact.js (delay/ground stop assessment)
// - WorldMap.jsx (RainViewer radar overlay)
//
// Old simulated weather code moved to: src/_deprecated/systems/
// ============================================================================

// Deduplicate news items by text, prioritizing new items
function deduplicateNews(newItems, existingItems) {
  const seen = new Set();
  const result = [];
  
  // Add new items first (they take priority)
  for (const item of newItems) {
    if (item?.text && !seen.has(item.text)) {
      seen.add(item.text);
      result.push(item);
    }
  }
  
  // Add existing items that aren't duplicates
  for (const item of existingItems) {
    if (item?.text && !seen.has(item.text)) {
      seen.add(item.text);
      result.push(item);
    }
  }
  
  // Limit to 10 items
  return result.slice(0, 10);
}

// ============================================================================
// SYSTEMS INTEGRATION - Macro, Progression, Crew, Billing
// ============================================================================

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

// Simplified macro state
function createMacroState() {
  return {
    currentMonth: 1,
    currentYear: 1,
    global: { GCI: 0.2, OPVI: 0.35, ARI: 0.45, CSI: 0.4 },
    jetFuelPriceMultiplier: 1.0,
    laborCostMultiplier: 1.0,
    demandMultiplier: 1.0,
    lastTickMonth: 0,
  };
}

function tickMacroMonth(macroState) {
  if (!macroState) macroState = createMacroState();
  const state = { ...macroState };
  
  // Drift indices slightly
  state.global = {
    GCI: clamp(state.global.GCI + (Math.random() - 0.5) * 0.05, 0, 1),
    OPVI: clamp(state.global.OPVI + (Math.random() - 0.5) * 0.08, 0, 1),
    ARI: clamp(state.global.ARI + (Math.random() - 0.5) * 0.05, 0, 1),
    CSI: clamp(state.global.CSI + (Math.random() - 0.5) * 0.04, 0, 1),
  };
  
  state.jetFuelPriceMultiplier = 0.7 + (state.global.OPVI * 1.1);
  state.laborCostMultiplier = 1.0 + (state.global.GCI * 0.2);
  state.demandMultiplier = 1.0 - (state.global.GCI * 0.3);
  
  state.currentMonth++;
  if (state.currentMonth > 12) {
    state.currentMonth = 1;
    state.currentYear++;
  }
  state.lastTickMonth = state.currentYear * 12 + state.currentMonth;
  
  return state;
}

// Progression check
const MILESTONES = [
  { id: "first_flight", name: "First Flight", check: (s) => s.stats?.completedFlights >= 1 },
  { id: "ten_flights", name: "Frequent Flyer", check: (s) => s.stats?.completedFlights >= 10 },
  { id: "hundred_flights", name: "Centurion", check: (s) => s.stats?.completedFlights >= 100 },
  { id: "million_revenue", name: "Millionaire", check: (s) => s.stats?.totalRevenue >= 1000000 },
  { id: "five_aircraft", name: "Fleet Builder", check: (s) => (s.airline?.fleet?.length || 0) >= 5 },
  { id: "ten_aircraft", name: "Regional Power", check: (s) => (s.airline?.fleet?.length || 0) >= 10 },
  { id: "thirty_aircraft", name: "Major Fleet", check: (s) => (s.airline?.fleet?.length || 0) >= 30 },
  { id: "eos_earned", name: "Elite Operator", check: (s) => s.eos?.certified === true },
];

// ============================================================================
// REPUTATION TIER SYSTEM (1-5) - Uses ReputationEngine.js
// Scale: 0-15000+ (Tier 1: 0-999, Tier 2: 1000-2999, Tier 3: 3000-6999, Tier 4: 7000-14999, Tier 5: 15000+)
// See TIER_THRESHOLDS and REPUTATION_POINTS imported from ReputationEngine.js
// ============================================================================

// Re-export for backwards compatibility
function getReputationTier(score) {
  return calculateTier(score);
}

// ============================================================================
// CREW ENGINE (FAR 117 Compliance)
// ============================================================================

const CREW_LIMITS = {
  maxFlightDutyPeriod: 14,    // hours
  maxFlightTime: 9,           // hours per duty period
  minRestPeriod: 10,          // hours
  maxDailyHours: 8,           // flight hours per day
  maxWeeklyHours: 60,         // flight hours per 7 days
  maxMonthlyHours: 100,       // flight hours per calendar month
};

function createCrewDutyState() {
  return {
    pilots: {},  // pilotId -> { hoursToday, hoursThisWeek, hoursThisMonth, status, lastRest }
    alerts: [],
  };
}

function updateCrewForFlight(crewState, flight, worldState) {
  if (!crewState) crewState = createCrewDutyState();
  if (!crewState.pilots) crewState.pilots = {};
  
  const flightHours = flight.blockHours || 2;
  const pilotId = flight.pilotId || `pilot_${flight.aircraftId}`;
  
  const pilot = crewState.pilots[pilotId] || {
    name: `Crew ${pilotId.slice(-4)}`,
    hoursToday: 0,
    hoursThisWeek: 0,
    hoursThisMonth: 0,
    status: "legal",
  };
  
  pilot.hoursToday += flightHours;
  pilot.hoursThisWeek += flightHours;
  pilot.hoursThisMonth += flightHours;
  
  const warnings = [];
  let isIllegal = false;
  
  // Check daily limit
  if (pilot.hoursToday > CREW_LIMITS.maxDailyHours) {
    warnings.push({ type: "critical", text: `CREW ILLEGAL: ${pilot.name} exceeded daily limit (${pilot.hoursToday.toFixed(1)}/${CREW_LIMITS.maxDailyHours}h)` });
    pilot.status = "illegal";
    isIllegal = true;
  }

  // Check weekly limit (warning at 90%)
  if (pilot.hoursThisWeek > CREW_LIMITS.maxWeeklyHours) {
    warnings.push({ type: "critical", text: `CREW ILLEGAL: ${pilot.name} exceeded weekly limit` });
    pilot.status = "illegal";
    isIllegal = true;
  } else if (pilot.hoursThisWeek > CREW_LIMITS.maxWeeklyHours * 0.9) {
    warnings.push({ type: "warning", text: `${pilot.name} approaching weekly limit (${pilot.hoursThisWeek.toFixed(0)}/${CREW_LIMITS.maxWeeklyHours}h)` });
  }

  // Check monthly limit
  if (pilot.hoursThisMonth > CREW_LIMITS.maxMonthlyHours) {
    warnings.push({ type: "critical", text: `CREW ILLEGAL: ${pilot.name} exceeded monthly limit` });
    pilot.status = "illegal";
    isIllegal = true;
  } else if (pilot.hoursThisMonth > CREW_LIMITS.maxMonthlyHours * 0.9) {
    warnings.push({ type: "warning", text: `${pilot.name} approaching monthly limit (${pilot.hoursThisMonth.toFixed(0)}/${CREW_LIMITS.maxMonthlyHours}h)` });
  }
  
  crewState.pilots[pilotId] = pilot;
  
  return { crewState, warnings, isIllegal };
}

function resetCrewDaily(crewState) {
  if (!crewState) return createCrewDutyState();
  if (!crewState.pilots) return { ...crewState, pilots: {} };
  
  for (const pilotId of Object.keys(crewState.pilots)) {
    crewState.pilots[pilotId].hoursToday = 0;
    if (crewState.pilots[pilotId].status === "illegal") {
      crewState.pilots[pilotId].status = "legal"; // Rest overnight
    }
  }
  return crewState;
}

function resetCrewWeekly(crewState) {
  if (!crewState) return createCrewDutyState();
  if (!crewState.pilots) return { ...crewState, pilots: {} };
  
  for (const pilotId of Object.keys(crewState.pilots)) {
    crewState.pilots[pilotId].hoursThisWeek = 0;
  }
  return crewState;
}

function resetCrewMonthly(crewState) {
  if (!crewState) return createCrewDutyState();
  if (!crewState.pilots) return { ...crewState, pilots: {} };
  
  for (const pilotId of Object.keys(crewState.pilots)) {
    crewState.pilots[pilotId].hoursThisMonth = 0;
  }
  return crewState;
}

// ============================================================================
// MAINTENANCE SYSTEM (A & C Checks)
// ============================================================================

const MAINTENANCE_INTERVALS = {
  A_CHECK: { hours: 500, days: 60, cost: 50000, duration: 1 },      // 1 day
  C_CHECK: { hours: 5000, days: 450, cost: 500000, duration: 14 },  // 14 days
};

function createMaintenanceState() {
  return {
    aircraft: {},  // aircraftId -> { lastACheck, lastCCheck, flightHours, status }
  };
}

function checkMaintenanceDue(maintenanceState, aircraft, gameTime) {
  if (!maintenanceState?.aircraft) return { aCheckDue: false, cCheckDue: false, grounded: false };
  
  const record = maintenanceState.aircraft[aircraft.id] || {
    lastACheck: gameTime,
    lastCCheck: gameTime,
    flightHours: 0,
    hoursSinceA: 0,
    hoursSinceC: 0,
  };
  
  const now = new Date(gameTime);
  const lastA = new Date(record.lastACheck);
  const lastC = new Date(record.lastCCheck);
  
  const daysSinceA = (now - lastA) / (24 * 60 * 60 * 1000);
  const daysSinceC = (now - lastC) / (24 * 60 * 60 * 1000);
  
  const aCheckDue = daysSinceA >= MAINTENANCE_INTERVALS.A_CHECK.days || 
                    record.hoursSinceA >= MAINTENANCE_INTERVALS.A_CHECK.hours;
  const cCheckDue = daysSinceC >= MAINTENANCE_INTERVALS.C_CHECK.days || 
                    record.hoursSinceC >= MAINTENANCE_INTERVALS.C_CHECK.hours;
  
  // Grounded if overdue by 10% of interval
  const aCheckOverdue = daysSinceA >= MAINTENANCE_INTERVALS.A_CHECK.days * 1.1;
  const cCheckOverdue = daysSinceC >= MAINTENANCE_INTERVALS.C_CHECK.days * 1.1;
  const grounded = aCheckOverdue || cCheckOverdue;
  
  return { aCheckDue, cCheckDue, grounded, daysSinceA, daysSinceC, record };
}

function updateMaintenanceHours(maintenanceState, aircraftId, flightHours, gameTime) {
  if (!maintenanceState) maintenanceState = createMaintenanceState();
  if (!maintenanceState.aircraft) maintenanceState.aircraft = {};
  
  const record = maintenanceState.aircraft[aircraftId] || {
    lastACheck: gameTime,
    lastCCheck: gameTime,
    flightHours: 0,
    hoursSinceA: 0,
    hoursSinceC: 0,
  };
  
  record.flightHours += flightHours;
  record.hoursSinceA += flightHours;
  record.hoursSinceC += flightHours;
  
  maintenanceState.aircraft[aircraftId] = record;
  return maintenanceState;
}

function performMaintenance(maintenanceState, aircraftId, checkType, gameTime) {
  if (!maintenanceState?.aircraft?.[aircraftId]) return maintenanceState;
  
  if (checkType === "A_CHECK") {
    maintenanceState.aircraft[aircraftId].lastACheck = gameTime;
    maintenanceState.aircraft[aircraftId].hoursSinceA = 0;
  } else if (checkType === "C_CHECK") {
    maintenanceState.aircraft[aircraftId].lastCCheck = gameTime;
    maintenanceState.aircraft[aircraftId].hoursSinceC = 0;
    // C check also resets A check
    maintenanceState.aircraft[aircraftId].lastACheck = gameTime;
    maintenanceState.aircraft[aircraftId].hoursSinceA = 0;
  }
  
  return maintenanceState;
}

// ============================================================================
// EOS (Elite Operator Status) TRACKING
// ============================================================================
// Now using EOSSystem.js - proper 14-day consecutive qualification
// with 85% OTP, zero cancellations, zero safety incidents, positive cashflow
// See: src/systems/EOSSystem.js for full implementation
// ============================================================================

// Local createEOSState for backwards compatibility with existing code
function createEOSState() {
  return createEOSStateFromSystem();
}

// Wrapper to process EOS daily and format news for game loop
function updateEOSProgress(worldState, gameTime) {
  const { eos, events, unlocks } = processEOSDaily(worldState, gameTime);

  // Convert EOS events to news format
  const news = events.map(event => {
    let type = "info";
    if (event.severity === "critical") type = "critical";
    else if (event.severity === "warning") type = "warning";
    else if (event.severity === "success") type = "success";

    // Format message with type prefix
    let text = event.message;
    if (event.type === "EOS_COLLAPSE") {
      text = `EOS COLLAPSED: ${event.message}`;
    } else if (event.type === "EOS_GRANTED") {
      text = `EOS GRANTED: ${event.message}`;
    } else if (event.type === "EOS_PROGRESS") {
      text = `EOS PROGRESS: ${event.message}`;
    } else if (event.type === "EOS_RESET") {
      text = `EOS RESET: ${event.message}`;
    } else if (event.type === "EOS_COOLDOWN") {
      text = `EOS COOLDOWN: ${event.message}`;
    }

    return { type, text, isToast: event.isToast };
  });

  return { eos, news, unlocks };
}

// ============================================================================
// A380 RARE ROLL SYSTEM - Now using EOSSystem.js
// ============================================================================
// Roll probabilities (fixed, non-scaling):
//   - Epic: 89.5%
//   - Legendary: 10%
//   - Mythic: 0.5%
// See RARE_AIRCRAFT in EOSSystem.js for aircraft database
// ============================================================================

// Re-export processA380Purchase from EOSSystem for external use
function purchaseA380(worldState) {
  return processA380Purchase(worldState);
}

// ============================================================================
// MILESTONE & AOC CHECK (NOT tier - tier is computed from reputation)
// This tracks: milestones (first flight, 100 flights, etc.) and AOC upgrades
// ============================================================================

function checkProgression(worldState) {
  // NOTE: "progression" here is milestones/AOC, NOT reputation tier
  // Reputation tier = calculateTier(reputationScore) - always computed
  const progression = worldState.progression || { unlockedMilestones: [], totalXP: 0 };
  const updates = { ...progression };
  const news = [];
  
  for (const milestone of MILESTONES) {
    if (!progression.unlockedMilestones?.includes(milestone.id)) {
      try {
        if (milestone.check(worldState)) {
          updates.unlockedMilestones = [...(updates.unlockedMilestones || []), milestone.id];
          updates.totalXP = (updates.totalXP || 0) + 100;
          news.push({ type: "success", text: `MILESTONE: ${milestone.name}` });
        }
      } catch (e) {}
    }
  }
  
  // AOC Tier progression (based on flights completed, not cash)
  const flights = worldState.stats?.completedFlights || 0;
  const currentAOC = updates.aocTier || progression.aocTier || 1;
  
  // AOC Tiers: 1=20 flights, 2=75 flights, 3=200 flights, 4=500 flights, 5=1000 flights
  let targetAOC = 1;
  if (flights >= 1000) targetAOC = 5;
  else if (flights >= 500) targetAOC = 4;
  else if (flights >= 200) targetAOC = 3;
  else if (flights >= 75) targetAOC = 2;
  else if (flights >= 20) targetAOC = 2;
  
  if (targetAOC > currentAOC && progression.aocAnnounced !== targetAOC) {
    updates.aocTier = targetAOC;
    updates.aocAnnounced = targetAOC;
    
    const aocNames = { 
      2: "Part 121 Regional", 
      3: "Part 121 Domestic", 
      4: "Part 121 International",
      5: "Part 121 Heavy"
    };
    const aocUnlocks = {
      2: "CRJ700, A220",
      3: "737MAX, A320",
      4: "787, A350",
      5: "747, A380 (requires EOS)",
    };
    news.push({ type: "success", text: `AOC UPGRADE: ${aocNames[targetAOC]}! Unlocked: ${aocUnlocks[targetAOC]}` });
  } else if (targetAOC > currentAOC) {
    updates.aocTier = targetAOC;
    updates.aocAnnounced = targetAOC;
  }
  
  return { progression: updates, news };
}

// Monthly billing
function calculateMonthlyBilling(worldState) {
  const stats = worldState.stats || {};
  const macro = worldState.macro || {};
  const fleetSize = worldState.airline?.fleet?.length || 1;
  const leasedCount = worldState.airline?.fleet?.filter(a => a.leased)?.length || 0;
  
  const revenue = stats.monthlyRevenue || 0;
  const fuelCosts = (stats.monthlyFuelBurn || 50000) * 3.50 * (macro.jetFuelPriceMultiplier || 1.0);
  const laborCosts = fleetSize * 45000 * (macro.laborCostMultiplier || 1.0);
  const maintenanceCosts = fleetSize * 25000;
  const leaseCosts = leasedCount * 150000; // $150K/month per leased aircraft
  const overheadCosts = 50000 + (fleetSize * 5000);
  const totalCosts = fuelCosts + laborCosts + maintenanceCosts + leaseCosts + overheadCosts;
  
  return { 
    revenue, 
    costs: totalCosts, 
    profit: revenue - totalCosts,
    breakdown: { fuel: fuelCosts, labor: laborCosts, maintenance: maintenanceCosts, leases: leaseCosts, overhead: overheadCosts }
  };
}

// Master tick
function tickAllSystems(worldState, gameTime) {
  const updates = {};
  const news = [];
  
  const currentDate = new Date(gameTime);
  const currentMonth = currentDate.getUTCMonth() + 1;
  const currentDay = currentDate.getUTCDate();
  const currentHour = currentDate.getUTCHours();
  const currentDayOfWeek = currentDate.getUTCDay(); // 0 = Sunday
  
  const lastTick = worldState._systemsLastTick || {};
  
  // ===== DAILY RESET (at midnight UTC) =====
  const currentDayKey = currentDate.toISOString().slice(0, 10);
  if (lastTick.day !== currentDayKey) {
    debug('%c╔════════════════════════════════════════════════════════════╗', 'color: #00ffff; font-weight: bold;');
    debug('%c║            📅 DAILY RESET TRIGGERED 📅                     ║', 'color: #00ffff; font-weight: bold; font-size: 14px;');
    debug('%c╠════════════════════════════════════════════════════════════╣', 'color: #00ffff; font-weight: bold;');
    debug(`%c║  Last Day: ${lastTick.day || 'NONE'}`, 'color: #ffff00;');
    debug(`%c║  New Day:  ${currentDayKey}`, 'color: #ffff00;');
    debug(`%c║  Hour:     ${currentHour}`, 'color: #ffff00;');
    debug(`%c║  Daily Flights Completed: ${worldState.stats?.dailyFlights || 0}`, 'color: #00ff00; font-weight: bold;');
    debug('%c╚════════════════════════════════════════════════════════════╝', 'color: #00ffff; font-weight: bold;');

    // Check for perfect day - bonus points handled by ReputationEngine.processEndOfDay
    // (NO_CANCELLATIONS_DAILY +10 and CLEAN_SAFETY_DAY +5 are auto-applied)
    if (worldState.stats?.dailyDelays === 0 && worldState.stats?.dailyCancellations === 0 && worldState.stats?.dailyFlights > 0) {
      news.push({ type: "success", text: "Perfect day: Zero delays, zero cancellations." });
    }
    
    // Reset crew daily hours
    updates.crewDuty = resetCrewDaily(worldState.crewDuty || createCrewDutyState());
    
    // Check maintenance across fleet
    let maintenanceAlerts = [];
    let maintenanceViolations = 0;
    for (const aircraft of (worldState.airline?.fleet || [])) {
      const status = checkMaintenanceDue(worldState.maintenance, aircraft, gameTime);
      if (status.grounded) {
        maintenanceViolations++;
        maintenanceAlerts.push({ type: "critical", text: `MAINTENANCE: ${aircraft.registration} GROUNDED - Overdue maintenance!` });
      } else if (status.aCheckDue) {
        maintenanceAlerts.push({ type: "warning", text: `MAINTENANCE: ${aircraft.registration} A-Check due (${Math.round(status.daysSinceA)} days)` });
      } else if (status.cCheckDue) {
        maintenanceAlerts.push({ type: "warning", text: `MAINTENANCE: ${aircraft.registration} C-Check due (${Math.round(status.daysSinceC)} days)` });
      }
    }
    news.push(...maintenanceAlerts.slice(0, 3)); // Limit maintenance spam
    updates.maintenance = { 
      ...(worldState.maintenance || createMaintenanceState()), 
      violations: maintenanceViolations 
    };
    
    // Update EOS progress (daily check)
    const { eos: eosUpdate, news: eosNews, unlocks: eosUnlocks } = updateEOSProgress(worldState, gameTime);
    updates.eos = eosUpdate;
    news.push(...eosNews);

    // Update zombie outbreak news (Passenger Zero effect)
    if (worldState.zombieOutbreakStarted) {
      const currentDay = worldState.dayNumber || 1;
      const outbreakStartDay = worldState.zombieOutbreakDay || 1;
      const daysSinceOutbreak = currentDay - outbreakStartDay;
      const newsSequence = worldState.zombieNewsSequence || [];

      let hasNewNews = false;
      const updatedSequence = newsSequence.map((item) => {
        if (item.day <= daysSinceOutbreak && !item.shown) {
          hasNewNews = true;
          news.push({ type: "zombie", text: item.text, severity: "high" });
          debug("[GameLoop] Zombie news day", daysSinceOutbreak, ":", item.text);
          return { ...item, shown: true };
        }
        return item;
      });

      if (hasNewNews) {
        updates.zombieNewsSequence = updatedSequence;
      }
    }

    // Apply permanent speed unlocks (never removed once granted)
    if (eosUnlocks && Object.keys(eosUnlocks).length > 0) {
      updates.unlocks = {
        ...(worldState.unlocks || {}),
        ...eosUnlocks,
      };
      debug('[GameLoop] SPEED UNLOCKS GRANTED:', eosUnlocks);
    }

    // ===============================================================
    // HUB NEGOTIATION TRIGGER - First hub at $50M cash (Simple Mode)
    // Two chances: if first fails, auto-retry after 7 game days
    // ===============================================================
    if (worldState.gameMode === 'simple' || worldState.gameMode === 'arcade') {
      const cash = worldState.airline?.cash || 0;
      const HUB_UNLOCK_CASH = 50000000; // $50 million
      const REGIONAL_HUBS = ['KSLC', 'KLAS', 'KSAN', 'KBNA', 'KAUS', 'KRDU', 'KCMH', 'KPIT', 'KSTL'];

      const existingHubs = worldState.airline?.hubs || ['KPDX'];
      const pendingInvites = worldState.pendingHubInvites || [];
      const completedAuctions = worldState.airline?.completedHubAuctions || [];
      const failedAuctions = worldState.airline?.failedHubAuctions || [];
      const firstHubAttempts = worldState.firstHubAttempts || 0;
      const firstHubFailedDay = worldState.firstHubFailedDay || 0;
      const currentDay = worldState.dayNumber || 1;

      // Check if player only has starting hub (hasn't won any hub yet)
      const onlyHasStartingHub = existingHubs.length === 1 && completedAuctions.length === 0;

      // FIRST HUB - Initial offer (attempt 0)
      if (cash >= HUB_UNLOCK_CASH && onlyHasStartingHub && firstHubAttempts === 0 && pendingInvites.length === 0) {
        const validHubs = REGIONAL_HUBS.filter(h => !existingHubs.includes(h));
        const randomHub = validHubs[Math.floor(Math.random() * validHubs.length)];

        debug(`[GameLoop] FIRST HUB INVITE (attempt 1): ${randomHub} - player hit $${(cash/1000000).toFixed(1)}M cash milestone!`);
        updates.pendingHubInvites = [randomHub];
        updates.lastHubInviteDay = currentDay;
        updates.firstHubAttempts = 1;
        news.push({
          type: "success",
          text: `HUB OPPORTUNITY: You've been invited to negotiate for a hub at ${randomHub}!`
        });
      }

      // FIRST HUB - Second chance (attempt 1 failed, 7 days passed)
      if (cash >= HUB_UNLOCK_CASH && onlyHasStartingHub && firstHubAttempts === 1 &&
          pendingInvites.length === 0 && firstHubFailedDay > 0 &&
          currentDay >= firstHubFailedDay + 7) {
        // Pick a DIFFERENT random regional hub
        const previousFailed = failedAuctions.map(f => f.hub);
        const validHubs = REGIONAL_HUBS.filter(h => !existingHubs.includes(h) && !previousFailed.includes(h));
        if (validHubs.length > 0) {
          const randomHub = validHubs[Math.floor(Math.random() * validHubs.length)];

          debug(`[GameLoop] FIRST HUB INVITE (attempt 2 - second chance): ${randomHub}`);
          updates.pendingHubInvites = [randomHub];
          updates.lastHubInviteDay = currentDay;
          updates.firstHubAttempts = 2;
          updates.firstHubFailedDay = 0; // Clear the timer
          news.push({
            type: "success",
            text: `SECOND CHANCE: Another hub opportunity at ${randomHub}!`
          });
        }
      }

      // ===============================================================
      // PROCESS QUEUED HUB INVITES (max 1 per day from queue)
      // ===============================================================
      const queuedInvites = worldState.queuedHubInvites || [];
      const currentPending = updates.pendingHubInvites || pendingInvites;
      const lastInviteDay = updates.lastHubInviteDay ?? worldState.lastHubInviteDay ?? 0;

      // If we haven't sent an invite today and there are queued invites, pop one
      if (queuedInvites.length > 0 && lastInviteDay < currentDay && currentPending.length === 0) {
        const nextHub = queuedInvites[0];
        const hubValue = getHubBaseValue(nextHub);

        // Only send if player still has enough cash
        if (cash >= hubValue) {
          debug(`[GameLoop] HUB INVITE (from queue): ${nextHub}`);
          updates.pendingHubInvites = [nextHub];
          updates.queuedHubInvites = queuedInvites.slice(1);
          updates.lastHubInviteDay = currentDay;
          news.push({
            type: "success",
            text: `HUB OPPORTUNITY: You've been invited to negotiate for a hub at ${nextHub}!`
          });
        } else {
          // Player lost cash, remove from queue
          debug(`[GameLoop] HUB REMOVED FROM QUEUE: ${nextHub} - player no longer has $${(hubValue/1000000).toFixed(1)}M`);
          updates.queuedHubInvites = queuedInvites.slice(1);
        }
      }
    }

    // Track reputation tier changes
    const oldRepTier = getReputationTier(worldState.reputation?.currentScore || 0);
    const newRepTier = getReputationTier(updates.reputation?.currentScore || worldState.reputation?.currentScore || 0);
    if (newRepTier !== oldRepTier) {
      const tierInfo = TIER_THRESHOLDS[newRepTier];
      if (newRepTier > oldRepTier) {
        news.push({ type: "success", text: `REPUTATION UP: Tier ${newRepTier} - ${tierInfo.name}` });
      } else {
        news.push({ type: "warning", text: `REPUTATION DOWN: Tier ${newRepTier} - ${tierInfo.name}` });
      }
    }

    // ===============================================================
    // TIER IS COMPUTED - No progression state needed
    // Tier = calculateTier(reputationScore) - always derived, never stored
    // ===============================================================

    // LOG DAILY FINANCIALS for ProfitChart before resetting
    const dailyRevenue = worldState.stats?.dailyRevenue || 0;
    const dailyCosts = worldState.stats?.dailyCosts || 0;
    const dailyRecord = {
      day: worldState.dayNumber || 1,
      date: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      netProfit: dailyRevenue - dailyCosts,
      revenue: dailyRevenue,
      costs: dailyCosts,
      flights: worldState.stats?.dailyFlights || 0,
    };

    const financialHistory = [...(worldState.financialHistory || []), dailyRecord];
    // Keep last 365 days max
    if (financialHistory.length > 365) {
      financialHistory.shift();
    }
    updates.financialHistory = financialHistory;

    // Reset daily stats
    updates.stats = {
      ...worldState.stats,
      dailyDelays: 0,
      dailyCancellations: 0,
      dailyFlights: 0,
      dailyDepartures: 0,
      dailyRevenue: 0,
      dailyCosts: 0,
    };

    // Update fuel spot price daily (Simulation Mode only)
    if (worldState.gameMode === 'simulation' && worldState.fuel) {
      updates.fuel = tickFuelPriceDaily(worldState.fuel, worldState.macro, gameTime);

      // Log fuel price changes for news feed
      const priceChange = updates.fuel.spotPrice - (worldState.fuel.spotPrice || 2.95);
      if (Math.abs(priceChange) > 0.10) {
        if (priceChange > 0) {
          news.push({ type: "warning", text: `FUEL: Spot price up to $${updates.fuel.spotPrice.toFixed(2)}/gal (+$${priceChange.toFixed(2)})` });
        } else {
          news.push({ type: "success", text: `FUEL: Spot price down to $${updates.fuel.spotPrice.toFixed(2)}/gal ($${priceChange.toFixed(2)})` });
        }
      }
    }

    // Update fuel spot price daily (Simple Mode - Strategic Fuel Trading)
    if (worldState.gameMode === 'simple' || worldState.gameMode === 'arcade') {
      const fuelUpdates = processDailyFuelSimple(worldState);
      Object.assign(updates, fuelUpdates);

      // Log significant price changes for news feed
      const oldPrice = worldState.fuelSpotPrice || 3.00;
      const priceChange = fuelUpdates.fuelSpotPrice - oldPrice;
      if (Math.abs(priceChange) > 0.10) {
        const trendInfo = fuelUpdates.fuelMarketTrend;
        const trendLabel = trendInfo?.direction === 'inflationary' ? '📈' :
                          trendInfo?.direction === 'deflationary' ? '📉' : '➡️';
        if (priceChange > 0) {
          news.push({ type: "warning", text: `${trendLabel} FUEL: Spot price up to $${fuelUpdates.fuelSpotPrice.toFixed(2)}/gal (+$${priceChange.toFixed(2)})` });
        } else {
          news.push({ type: "success", text: `${trendLabel} FUEL: Spot price down to $${fuelUpdates.fuelSpotPrice.toFixed(2)}/gal ($${priceChange.toFixed(2)})` });
        }
      }
    }

    updates._systemsLastTick = { ...lastTick, day: currentDayKey };
  }

  // ===== WEEKLY RESET (Sunday at 0400) =====
  const weekKey = `${currentDate.getUTCFullYear()}-W${Math.floor(currentDate.getTime() / (7 * 24 * 60 * 60 * 1000))}`;
  if (lastTick.week !== weekKey && currentDayOfWeek === 0 && currentHour >= 4) {
    updates.crewDuty = resetCrewWeekly(updates.crewDuty || worldState.crewDuty || createCrewDutyState());
    news.push({ type: "info", text: "Weekly crew hours reset" });
    updates._systemsLastTick = { ...(updates._systemsLastTick || lastTick), week: weekKey };
  }

  // ===== MONTHLY TICK (1st of month at 0400) =====
  const monthKey = `${currentDate.getUTCFullYear()}-${currentMonth}`;
  if (lastTick.month !== monthKey && currentDay === 1 && currentHour >= 4) {
    // Tick macro engine
    updates.macro = tickMacroMonth(worldState.macro);

    // Calculate monthly billing
    const billing = calculateMonthlyBilling(worldState);
    updates.financials = { ...worldState.financials, lastMonthBilling: billing };
    let monthlyCash = (worldState.airline?.cash || 0) + billing.profit;

    // ===============================================================
    // PROCESS MONTHLY SYSTEM COSTS
    // Tech, Consultants, CrewMarket, Advertising, SupportCrew
    // ===============================================================
    let totalSystemCosts = 0;

    // Tech Systems monthly maintenance
    if (worldState.tech) {
      const techResult = processMonthlyTechCosts(worldState.tech);
      updates.tech = techResult.state;
      totalSystemCosts += techResult.totalCost;
    }

    // Consultants monthly retainers
    if (worldState.consultants) {
      const consultantResult = processMonthlyConsultantCosts(worldState.consultants);
      updates.consultants = consultantResult.state;
      totalSystemCosts += consultantResult.totalCost;
    }

    // Crew Market (flight crew payroll)
    if (worldState.crewMarket) {
      const crewMarketResult = processMonthlyCrewCosts(worldState.crewMarket);
      updates.crewMarket = crewMarketResult.state;
      totalSystemCosts += crewMarketResult.totalCost;
    }

    // Advertising campaigns
    if (worldState.advertising) {
      const adResult = processMonthlyAdvertising(worldState.advertising, worldState.relations, worldState);
      updates.advertising = adResult.advertisingState;
      if (adResult.relationsState) {
        updates.relations = adResult.relationsState;
      }
      totalSystemCosts += adResult.totalCost || 0;
    }

    // Fuel System monthly processing (Simulation Mode only)
    // - Activate pending hedges
    // - Check contract minimums
    // - Apply penalties
    if (worldState.gameMode === 'simulation' && worldState.fuel) {
      const fuelResult = processMonthlyFuel(
        updates.fuel || worldState.fuel,
        currentMonth,
        gameTime
      );
      updates.fuel = fuelResult.newFuelState;

      // Apply contract minimum penalties
      if (fuelResult.penalties && fuelResult.penalties.length > 0) {
        let totalPenalties = 0;
        for (const penalty of fuelResult.penalties) {
          totalPenalties += penalty.penalty;
          news.push({
            type: "warning",
            text: `FUEL: ${penalty.supplier} minimum not met (-$${(penalty.penalty / 1000).toFixed(0)}K penalty)`
          });
        }
        totalSystemCosts += totalPenalties;
      }

      // Report fuel savings if significant
      const totalSavings = (updates.fuel.contractSavings || 0) + (updates.fuel.hedgeSavings || 0);
      if (totalSavings > 10000) {
        news.push({
          type: "success",
          text: `FUEL: Saved $${(totalSavings / 1000).toFixed(0)}K via contracts & hedges`
        });
      }
    }

    // Support Crew (mechanics, fleet ops, engineers) - NOT MONTHLY, they're already in payroll
    // Just track their modifiers affect maintenance costs
    const maintenanceModifier = getMaintenanceCostModifier(worldState.supportCrew);
    const adjustedMaintCosts = Math.round((billing.costs * 0.3) * maintenanceModifier); // Assume 30% of costs are maintenance
    const maintSavings = Math.round((billing.costs * 0.3) - adjustedMaintCosts);
    if (maintSavings > 0) {
      totalSystemCosts -= maintSavings; // Reduce costs
    }

    monthlyCash -= totalSystemCosts;
    updates.airline = { ...worldState.airline, cash: monthlyCash };

    if (totalSystemCosts > 0) {
      news.push({ type: "info", text: `SYSTEMS: Costs $${(totalSystemCosts / 1000).toFixed(0)}K (Tech, Staff, Marketing)` });
    }
    if (maintSavings > 0) {
      news.push({ type: "success", text: `MAINTENANCE: Savings $${(maintSavings / 1000).toFixed(0)}K (Support crew efficiency)` });
    }

    // Reset crew monthly hours
    updates.crewDuty = resetCrewMonthly(updates.crewDuty || worldState.crewDuty || createCrewDutyState());

    // Reset monthly stats
    updates.stats = { ...(updates.stats || worldState.stats), monthlyRevenue: 0, monthlyFuelBurn: 0, monthlyFlights: 0 };

    // Monthly report
    if (billing.profit > 0) {
      news.push({ type: "success", text: `MONTHLY: +$${(billing.profit / 1000).toFixed(0)}K profit | Revenue: $${(billing.revenue / 1000).toFixed(0)}K` });
    } else {
      news.push({ type: "warning", text: `MONTHLY: -$${(Math.abs(billing.profit) / 1000).toFixed(0)}K loss | Costs: $${(billing.costs / 1000).toFixed(0)}K` });
    }

    // Fuel price news
    if (updates.macro?.jetFuelPriceMultiplier > 1.2) {
      news.push({ type: "warning", text: `FUEL: Prices elevated +${((updates.macro.jetFuelPriceMultiplier - 1) * 100).toFixed(0)}% above baseline` });
    } else if (updates.macro?.jetFuelPriceMultiplier < 0.85) {
      news.push({ type: "success", text: `FUEL: Prices low ${((1 - updates.macro.jetFuelPriceMultiplier) * 100).toFixed(0)}% below baseline` });
    }
    
    updates._systemsLastTick = { ...(updates._systemsLastTick || lastTick), month: monthKey };
  }
  
  // ===== ALWAYS: Check progression =====
  const { progression, news: progNews } = checkProgression({ ...worldState, ...updates });
  if (progNews.length > 0) {
    updates.progression = progression;
    news.push(...progNews);
  }
  
  return { updates, news };
}

// ============================================================================
// ROUTE CALCULATIONS - NOW WITH MACRO MODIFIERS
// ============================================================================

export function calculateFlightTime(distanceNm, aircraftType = "E175", blockTimeMultiplier = 1.0) {
  // Try to get speed from aircraft database
  const aircraft = AIRCRAFT_DATABASE[aircraftType];
  const cruiseSpeed = aircraft?.cruiseSpeed || 450;
  const cruiseTime = distanceNm / cruiseSpeed; // hours
  const climbDescent = 0.5; // 30 min for climb + descent
  const baseTime = cruiseTime + climbDescent;
  
  // Apply macro multiplier (ARI causes reroutes = longer flights)
  return baseTime * blockTimeMultiplier;
}

export function calculateEarnings(flight, worldState) {
  if (!flight || !worldState) return { passengers: 0, loadFactor: 0, revenue: 0, costs: 0, profit: 0, blockHours: 0, distance: 0 };

  const distance = calculateDistance(flight.origin, flight.destination);

  // BUG-3 FIX: Guard against NaN distance - return zero earnings for invalid flights
  if (!Number.isFinite(distance) || distance <= 0) {
    console.warn(`[EARNINGS] Invalid distance (${distance}) for ${flight.origin}->${flight.destination}`);
    return { passengers: 0, loadFactor: 0, revenue: 0, costs: 0, profit: 0, blockHours: 0, distance: 0 };
  }

  // Get macro modifiers if available
  let demandMultiplier = 1.0;
  let costMultiplier = 1.0;
  let blockTimeMultiplier = 1.0;
  let fuelPriceMultiplier = 1.0;

  // Get fuel price from macro state if available
  if (worldState.macro?.jetFuelPriceMultiplier) {
    fuelPriceMultiplier = worldState.macro.jetFuelPriceMultiplier;
  }

  const flightHours = calculateFlightTime(distance, flight.aircraftType, blockTimeMultiplier);

  const aircraft = worldState.aircraft?.find((a) => a?.id === flight.aircraftId);
  const aircraftData = AIRCRAFT_DATABASE[flight.aircraftType] || AIRCRAFT_DATABASE["E175"];
  const capacity = aircraft?.capacity || aircraftData?.capacity || 180;
  const isWidebody = aircraftData?.category === "widebody";

  // Check if international (different countries)
  // Check both limited AIRPORTS and FULL_AIRPORTS for global routes
  const originAirport = AIRPORTS[flight.origin] || FULL_AIRPORTS[flight.origin];
  const destAirport = AIRPORTS[flight.destination] || FULL_AIRPORTS[flight.destination];
  const isInternational = originAirport?.country !== destAirport?.country ||
                          originAirport?.international !== destAirport?.international;
  const isTransoceanic = distance > 3000; // Rough proxy for ocean crossing

  // Demand-based load factor - international long-haul typically runs higher
  const isHub = originAirport?.hubSize === "large" || destAirport?.hubSize === "large";
  const baseLoadFactor = isTransoceanic ? 0.85 : isHub ? 0.82 : 0.72;
  const randomVariance = 0.9 + Math.random() * 0.2;
  const loadFactor = Math.min(0.98, baseLoadFactor * randomVariance * demandMultiplier);

  const passengers = Math.floor(capacity * loadFactor);

  // ============================================================================
  // REVENUE - Scales with distance, aircraft type, and route type
  // ============================================================================
  // Base yield per passenger based on distance
  // Short-haul has higher per-mile yield (convenience premium, fixed costs spread over fewer miles)
  // Long-haul has economies of scale but higher absolute fares
  let baseYield;
  if (distance < 400) {
    baseYield = 180 + (distance * 0.05); // $180-200 for short regionals (convenience premium)
  } else if (distance < 800) {
    baseYield = 200 + ((distance - 400) * 0.10); // $200-240 for regionals
  } else if (distance < 1500) {
    baseYield = 240 + ((distance - 800) * 0.12); // $240-324 for domestic
  } else if (distance < 3000) {
    baseYield = 324 + ((distance - 1500) * 0.08); // $324-444 for medium-haul
  } else if (distance < 5000) {
    baseYield = 444 + ((distance - 3000) * 0.06); // $444-564 for long-haul
  } else if (distance < 8000) {
    baseYield = 564 + ((distance - 5000) * 0.04); // $564-684 for ultra long-haul
  } else {
    baseYield = 684 + ((distance - 8000) * 0.02); // $684+ for extreme range
  }

  // Widebody premium - business/first class cabin generates more revenue
  const cabinPremium = isWidebody ? 1.35 : 1.0; // 35% premium for widebody (mixed cabin)

  // International premium - higher fares for crossing borders
  const internationalPremium = isInternational ? 1.15 : 1.0;

  // Transoceanic premium - Atlantic/Pacific crossings command premium fares
  const transoceanicPremium = isTransoceanic ? 1.20 : 1.0;

  // Reputation affects yield
  const reputationMultiplier = 0.7 + ((worldState.reputation?.currentScore || 50) / 100) * 0.6;

  const yieldPerPax = baseYield * cabinPremium * internationalPremium * transoceanicPremium * reputationMultiplier;

  const ticketRevenue = passengers * yieldPerPax;
  // Ancillary scales with distance (long-haul has more services)
  const ancillaryPerPax = isTransoceanic ? 65 : isInternational ? 50 : 35;
  const ancillaryRevenue = passengers * ancillaryPerPax;
  const totalRevenue = ticketRevenue + ancillaryRevenue;

  // ============================================================================
  // COSTS - Uses aircraft-specific operating costs
  // ============================================================================
  // Fuel cost - uses aircraft's actual fuel burn
  const fuelBurn = aircraftData?.fuelBurn || 2400; // kg/hour
  const totalFuelKg = fuelBurn * flightHours;
  // Convert kg to gallons (Jet-A: ~3.04 kg/gallon)
  const totalFuelGallons = totalFuelKg / 3.04;

  // Get effective fuel price from contracts/hedges/spot market (Simulation Mode only)
  let fuelPrice;
  let fuelSource = 'SPOT';
  if (worldState.gameMode === 'simulation' && worldState.fuel) {
    const fuelInfo = getEffectiveFuelPrice(worldState.fuel, flight.origin, totalFuelGallons);
    // Convert $/gallon to $/kg for consistency
    fuelPrice = fuelInfo.price / 3.04;
    fuelSource = fuelInfo.source;
  } else {
    // Simple mode fallback - use macro multiplier
    const baseFuelPrice = 0.95; // $/kg baseline
    fuelPrice = baseFuelPrice * fuelPriceMultiplier;
  }
  const fuelCost = totalFuelKg * fuelPrice;

  // Operating cost from aircraft database (includes crew, maintenance baseline)
  const hourlyOpCost = aircraftData?.operatingCost || 3500;
  const operatingCost = hourlyOpCost * flightHours;

  // Additional maintenance per cycle (takeoff/landing wear)
  const isRegional = aircraftData?.category === "regional";
  const cycleCost = isWidebody ? 2500 : isRegional ? 400 : 800;

  // Ground handling scales with aircraft size
  const groundHandling = isWidebody ? 8000 : isRegional ? 1800 : 3500;

  // Navigation fees scale with distance
  const navFees = distance * 0.15;

  // Landing fees scale with aircraft weight/size and airport
  const baseLandingFee = isWidebody ? 5500 : isRegional ? 1200 : 2200;
  const originFee = baseLandingFee * (originAirport?.slotCostMult || 1.0);
  const destFee = baseLandingFee * (destAirport?.slotCostMult || 1.0);
  const airportFees = originFee + destFee;

  // Overflight fees for international
  const overflightFees = isInternational ? distance * 0.08 : 0;

  const baseCosts = fuelCost + operatingCost + cycleCost + groundHandling + navFees + airportFees + overflightFees;
  const totalCosts = baseCosts * costMultiplier;
  const profit = totalRevenue - totalCosts;

  return {
    passengers,
    loadFactor,
    revenue: Math.round(totalRevenue),
    costs: Math.round(totalCosts),
    profit: Math.round(profit),
    blockHours: flightHours,
    distance,
    // Debug info
    breakdown: {
      yieldPerPax: Math.round(yieldPerPax),
      fuelCost: Math.round(fuelCost),
      fuelSource,
      fuelGallons: Math.round(totalFuelGallons),
      operatingCost: Math.round(operatingCost),
      airportFees: Math.round(airportFees),
      groundHandling,
      isRegional,
      isWidebody,
      isInternational,
      isTransoceanic,
    },
    macroInfluence: {
      demandMultiplier,
      costMultiplier,
      fuelPriceMultiplier,
      blockTimeMultiplier,
    },
  };
}

// ============================================================================
// FLIGHT PHASES
// ============================================================================

const FLIGHT_PHASES = {
  scheduled: { next: "boarding", durationMin: 0 },
  boarding: { next: "pushback", durationMin: 25 },
  pushback: { next: "taxi_out", durationMin: 5 },
  taxi_out: { next: "takeoff", durationMin: 12 },
  takeoff: { next: "climb", durationMin: 2 },
  climb: { next: "cruise", durationMin: 15 },
  cruise: { next: "descent", durationMin: null }, // calculated
  descent: { next: "approach", durationMin: 18 },
  approach: { next: "landing", durationMin: 8 },
  landing: { next: "taxi_in", durationMin: 1 },
  taxi_in: { next: "arrived", durationMin: 8 },
  arrived: { next: "completed", durationMin: 2 },
  completed: { next: null, durationMin: 0 },
};

// ============================================================================
// DISTANCE-BASED PHASE DERIVATION
// Phase is a PURE FUNCTION of distance fraction - NOT time
// ============================================================================

function getPhaseFromDistanceFraction(fraction, totalDistance = 1000) {
  // Note: No taxi_out - flights auto-launch directly to takeoff, skipping ground ops
  // Early phases use percentage (takeoff/climb are proportional to flight length)
  if (fraction < 0.08) return "takeoff";
  if (fraction < 0.20) return "climb";

  // Final phases use ABSOLUTE DISTANCE remaining (not percentage)
  // This prevents long flights from showing "taxi_in" while still 50nm out
  const distanceRemaining = totalDistance * (1 - fraction);

  // Descent starts ~120nm from destination (typical for jets)
  if (distanceRemaining > 120) return "cruise";
  // Approach within ~30nm
  if (distanceRemaining > 30) return "descent";
  // Landing within ~10nm
  if (distanceRemaining > 10) return "approach";
  // Taxi-in within ~2nm (at the airport)
  if (distanceRemaining > 2) return "landing";
  return "taxi_in";
}

// Ground ops phases - these are handled by player button clicks in OpsCenter
// GameLoopEngine should NOT auto-progress these
const GROUND_OPS_PHASES = ["boarding", "pushback", "taxi_out"];

// Airborne phases - these are auto-progressed by GameLoopEngine
const AIRBORNE_PHASES = ["takeoff", "climb", "cruise", "descent", "approach", "landing", "taxi_in", "arrived"];

// ============================================================================
// EMERGENCY LANDING SYSTEM (Fuel Exhaustion)
// ============================================================================

/**
 * Find the nearest airport to a given position
 * Returns distance in nm and airport data
 */
function findNearestAirport(lat, lon) {
  let nearestAirport = null;
  let nearestDistance = Infinity;

  // Check all airports in both databases
  const allAirports = { ...AIRPORTS, ...FULL_AIRPORTS };

  for (const [code, airport] of Object.entries(allAirports)) {
    if (!airport.lat || !airport.lon) continue;

    // Haversine formula for distance
    const R = 3440.065; // Earth radius in nautical miles
    const lat1 = lat * Math.PI / 180;
    const lat2 = airport.lat * Math.PI / 180;
    const dLat = (airport.lat - lat) * Math.PI / 180;
    const dLon = (airport.lon - lon) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;

    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestAirport = { code, ...airport };
    }
  }

  return { airport: nearestAirport, distanceNm: nearestDistance };
}

/**
 * Calculate emergency landing consequences
 * Severity depends on proximity to airports:
 * - Within 50nm of an airport: "bad" - manageable consequences
 * - Beyond 50nm: "catastrophic" - severe consequences
 */
export function calculateEmergencyLandingConsequences(flight, currentLat, currentLon, worldState) {
  const { airport: nearestAirport, distanceNm } = findNearestAirport(currentLat, currentLon);

  // Determine severity threshold
  const CLOSE_THRESHOLD_NM = 100; // Within 100nm = "bad" (successful divert), beyond = "catastrophic"
  const isClose = distanceNm <= CLOSE_THRESHOLD_NM;
  const severity = isClose ? "bad" : "catastrophic";

  // Get aircraft and route info
  const aircraft = worldState.aircraft?.find(a => a?.id === flight.aircraftId);
  const capacity = aircraft?.capacity || 180;
  const estimatedPassengers = Math.floor(capacity * 0.75);

  // Base costs
  let baseFAAPenalty = 500000;  // FAA fine for fuel emergency
  let basePaxCompensation = 400; // Per passenger compensation
  let baseRepHit = -100;        // Reputation damage
  let baseRelationsHit = -15;   // Airport relations damage
  let aircraftDamagePercent = 5; // Degradation from emergency landing

  // Apply severity multiplier
  if (severity === "catastrophic") {
    baseFAAPenalty = 2000000;     // $2M FAA fine
    basePaxCompensation = 1000;   // Full compensation + damages
    baseRepHit = -300;            // Massive reputation hit
    baseRelationsHit = -40;       // Severe relations damage
    aircraftDamagePercent = 25;   // Major aircraft damage from off-airport landing
  }

  // Calculate total costs
  const faaPenalty = baseFAAPenalty;
  const passengerCompensation = estimatedPassengers * basePaxCompensation;
  const hotelMeals = estimatedPassengers * (isClose ? 300 : 800); // Stranded passengers
  const aircraftRecoveryCost = isClose ? 50000 : 500000; // Getting aircraft back
  const mediaSettlement = severity === "catastrophic" ? 500000 : 50000; // PR damage control

  const totalFinancialCost = faaPenalty + passengerCompensation + hotelMeals + aircraftRecoveryCost + mediaSettlement;

  return {
    severity,
    isClose,
    distanceToNearestAirportNm: Math.round(distanceNm),
    nearestAirport: nearestAirport?.code || "UNKNOWN",
    nearestAirportCity: nearestAirport?.city || "Unknown Location",

    // Financial consequences
    totalCost: Math.round(totalFinancialCost),
    breakdown: {
      faaPenalty,
      passengerCompensation,
      hotelMeals,
      aircraftRecoveryCost,
      mediaSettlement,
    },

    // Reputation consequences
    reputationHit: baseRepHit,
    relationsHit: baseRelationsHit,

    // Aircraft consequences
    aircraftDamagePercent,

    // Passengers affected
    affectedPassengers: estimatedPassengers,

    // News message
    newsMessage: severity === "catastrophic"
      ? `CRITICAL FUEL EMERGENCY: ${flight.flightNumber} made emergency off-airport landing ${Math.round(distanceNm)}nm from ${nearestAirport?.code || 'nearest airport'}. All passengers safe but massive fines and compensation required.`
      : `FUEL EMERGENCY: ${flight.flightNumber} diverted to ${nearestAirport?.code || 'nearby airport'} due to fuel exhaustion. ${estimatedPassengers} passengers stranded, FAA investigation pending.`,
  };
}

// ============================================================================
// CANCELLATION ECONOMICS
// ============================================================================

const CANCELLATION_THRESHOLD_MINUTES = 30; // Cancel after +30 min past departure

/**
 * Calculate the financial and reputation cost of cancelling a flight
 */
export function calculateCancellationCost(flight, worldState) {
  const distance = calculateDistance(flight.origin, flight.destination);

  // BUG-3 FIX: Guard against NaN distance
  if (!Number.isFinite(distance) || distance <= 0) {
    // Return minimal fixed costs for invalid flights
    return 5000;
  }

  const aircraft = worldState.aircraft?.find((a) => a?.id === flight.aircraftId);
  const capacity = aircraft?.capacity || 76; // E175 default

  // Estimate how many passengers were booked (assume 75% load)
  const estimatedPassengers = Math.floor(capacity * 0.75);

  // Base ticket price estimate
  const baseYield = distance < 500 ? 150 : distance < 1000 ? 220 : distance < 2000 ? 350 : 500;
  const avgTicketPrice = baseYield;
  
  // COSTS:
  // 1. Full refunds to all passengers
  const refundCost = estimatedPassengers * avgTicketPrice;
  
  // 2. DOT compensation ($400 per passenger for cancellation)
  const dotCompensation = estimatedPassengers * 400;
  
  // 3. Rebooking costs (hotel, meals, rebooking fees) - ~$200 per stranded pax
  const rebookingCost = estimatedPassengers * 200;
  
  // 4. Crew repositioning cost (they're now out of position)
  const crewReposCost = 2500;
  
  // 5. Gate/slot penalties
  const gatePenalty = 1500;
  
  const totalCost = refundCost + dotCompensation + rebookingCost + crewReposCost + gatePenalty;
  
  // REPUTATION HIT:
  // Base -15 points, worse for longer routes and hub airports
  let reputationHit = -15;
  if (distance > 1000) reputationHit -= 5;
  const origApt = AIRPORTS[flight.origin] || FULL_AIRPORTS[flight.origin];
  const destApt = AIRPORTS[flight.destination] || FULL_AIRPORTS[flight.destination];
  if (origApt?.hubSize === "large" || destApt?.hubSize === "large") {
    reputationHit -= 10; // Hub cancellations are very visible
  }
  
  return {
    totalCost: Math.round(totalCost),
    refundCost: Math.round(refundCost),
    dotCompensation: Math.round(dotCompensation),
    rebookingCost: Math.round(rebookingCost),
    crewReposCost,
    gatePenalty,
    reputationHit,
    affectedPassengers: estimatedPassengers,
    reason: "Flight cancelled - no ground operations initiated",
  };
}

function getPhaseProgress(flight, gameTime) {
  if (!flight.phaseStartedAt) return 0;
  const elapsed = (new Date(gameTime) - new Date(flight.phaseStartedAt)) / 60000; // minutes
  const duration = flight.phaseDurationMin || 1;
  // Clamp between 0 and 1 - negative elapsed means time mismatch
  return Math.max(0, Math.min(1, elapsed / duration));
}

function advanceFlightPhase(flight, gameTime, worldState) {
  const currentPhase = FLIGHT_PHASES[flight.phase];
  if (!currentPhase || !currentPhase.next) return flight;

  const nextPhase = currentPhase.next;
  let phaseDuration = FLIGHT_PHASES[nextPhase]?.durationMin || 5;

  // Calculate cruise duration based on distance
  if (nextPhase === "cruise") {
    const distance = calculateDistance(flight.origin, flight.destination);

    // BUG-3 FIX: Guard against NaN distance - use minimum cruise time
    if (!Number.isFinite(distance) || distance <= 0) {
      phaseDuration = 10; // Minimum 10 minutes cruise
    } else {
      const flightHours = calculateFlightTime(distance, flight.aircraftType, 1.0);
      const cruiseHours = flightHours - 0.5; // subtract climb/descent
      phaseDuration = Math.max(10, cruiseHours * 60);
    }
  }

  // Random delay chance - uses macro state if available
  const groundPhases = ["boarding", "pushback", "taxi_out", "taxi_in"];
  if (groundPhases.includes(nextPhase)) {
    // Base 10% delay chance, boosted by macro conditions
    let delayChance = 0.1;
    const macro = worldState.macro;
    if (macro?.global) {
      // ARI (congestion) and CSI (weather) increase delay probability
      delayChance += (macro.global.ARI || 0) * 0.1;
      delayChance += (macro.global.CSI || 0) * 0.15;
    }

    if (Math.random() < delayChance) {
      const delayMinutes = Math.floor(Math.random() * 15) + 5;
      phaseDuration += delayMinutes;

      // Determine delay reason
      let delayReason = "Operational delay";
      if (macro?.global) {
        const ari = macro.global.ARI || 0;
        const csi = macro.global.CSI || 0;

        if (csi > 0.5 && Math.random() < 0.6) {
          delayReason = "Weather delay";
        } else if (ari > 0.5 && Math.random() < 0.5) {
          delayReason = "ATC congestion";
        } else if (Math.random() < 0.3) {
          delayReason = "Ground equipment";
        }
      }

      flight.delays = [...(flight.delays || []), { minutes: delayMinutes, reason: delayReason, timestamp: gameTime }];
      flight.totalDelayMinutes = (flight.totalDelayMinutes || 0) + delayMinutes;
    }
  }

  return {
    ...flight,
    phase: nextPhase,
    phaseStartedAt: gameTime,
    phaseDurationMin: phaseDuration,
    status: nextPhase === "completed" ? "completed" : "active",
  };
}

// ============================================================================
// GAME LOOP ENGINE
// ============================================================================

export default function GameLoopEngine() {
  const { worldState, setWorldState, completeFlight, addEvent } = useWorldState();
  const lastTickRef = useRef(Date.now());

  useEffect(() => {
    // Guard against missing worldState
    if (!worldState || !worldState.flights) {
      debug("GameLoopEngine: waiting for worldState");
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      let realDeltaMs = now - lastTickRef.current;
      lastTickRef.current = now;

      // Clamp realDeltaMs to prevent huge jumps when tab is inactive or lag spikes
      // Max 500ms real time per tick prevents flights from completing instantly
      // when switching back from another tab at high game speeds
      realDeltaMs = Math.min(realDeltaMs, 500);

      if (!worldState || worldState.isPaused) return;

      // ============================================================================
      // DECOUPLED TIME SYSTEM
      // ============================================================================
      // Clock and flights run on SEPARATE time scales:
      //
      // CLOCK (fixed): Always 128x = 11.25 real minutes per game day
      //   - Day/night cycle, schedules, daily triggers all use clockDeltaMs
      //   - Unaffected by speed slider - game day always takes ~11 real min
      //
      // FLIGHTS (variable): baseFlightScale * gameSpeed
      //   - Plane movement, flight progress uses flightDeltaMs
      //   - Speed slider (1x-16x) controls how fast planes fly
      //   - At 16x: flights at half clock speed (8 * 16 = 128x, matches clock)
      //   - At 8x: flights at quarter clock speed (8 * 8 = 64x)
      //   - At 1x: flights much slower than clock (8x)
      //
      // This lets players slow down flight animations when things get hectic
      // without slowing the overall game day progression.
      // ============================================================================

      const CLOCK_MULTIPLIER = 128; // Fixed: ~11 real min = 1 game day
      const baseFlightScale = 8;    // Base flight speed multiplier (16x = clock speed)
      // Speed cap: 4x always available, 12x unlocked with EOS (bypassed in devMode for testing)
      const hasEOS = worldState.eos?.hasEOS || false;
      const isDevMode = worldState.devMode || false;
      const maxSpeed = isDevMode ? 64 : (hasEOS ? 12 : 4);
      const gameSpeed = Math.min(worldState.gameSpeed || 1, maxSpeed);

      // Cap realDeltaMs to prevent clock skipping when frames take too long
      // NOTE: Tick interval is 100ms, so cap should allow the full tick plus some slack
      const cappedRealDeltaMs = Math.min(realDeltaMs, 150);

      // Clock delta - FIXED rate (128x), always ~11 min = 1 game day
      const clockDeltaMs = cappedRealDeltaMs * CLOCK_MULTIPLIER;

      // Flight delta - VARIABLE rate (baseFlightScale * gameSpeed)
      // At 16x slider: 8 * 16 = 128x (matches clock speed)
      // At lower speeds: flights move slower relative to clock
      const flightDeltaMs = cappedRealDeltaMs * baseFlightScale * gameSpeed;

      setWorldState((prev) => {
        if (!prev) return prev;

        // Advance game time using CLOCK delta (fixed rate - 10 min = 1 day)
        const oldGameTime = new Date(prev.gameTime);
        const newGameTime = new Date(oldGameTime.getTime() + clockDeltaMs);

        // =====================================================================
        // BUFF EXPIRATION CHECK (real-time based)
        // Check if any active buffs have expired and deactivate them
        // =====================================================================
        let buffUpdates = null;
        const now = Date.now();
        const buffs = prev.buffs || {};

        // Check halfPriceFuel buff
        if (buffs.halfPriceFuel?.active && buffs.halfPriceFuel?.expiresAt && now >= buffs.halfPriceFuel.expiresAt) {
          buffUpdates = buffUpdates || { ...buffs };
          buffUpdates.halfPriceFuel = { active: false, expiresAt: null, grantedAt: buffs.halfPriceFuel.grantedAt };
          debug('[GameLoop] BUFF EXPIRED: Half-price fuel');
        }

        // Check doubleRevenue buff
        if (buffs.doubleRevenue?.active && buffs.doubleRevenue?.expiresAt && now >= buffs.doubleRevenue.expiresAt) {
          buffUpdates = buffUpdates || { ...buffs };
          buffUpdates.doubleRevenue = { active: false, expiresAt: null, grantedAt: buffs.doubleRevenue.grantedAt };
          debug('[GameLoop] BUFF EXPIRED: Double revenue');
        }

        // Track cancellation events to add
        const newEvents = [];
        let cashChange = 0;
        let reputationChange = 0;
        let cancelledCount = 0;
        
        // Track completed flights for crew/stats updates
        const flightCompletions = [];

        // Track cancelled flights that need relations penalty applied
        const cancellationsToProcess = [];

        // Track emergency landings for consequence processing
        const emergencyLandingsToProcess = [];

        // Track diversions for cost processing
        const diversionsToProcess = [];

        // Track auto-cancellations for cost processing
        const autoCancellationsToProcess = [];

        // Track slot departures - when flights launch, update their slot to DEPARTED
        const slotDeparturesToProcess = [];

        // Track slot completions - when flights complete, update their slot to COMPLETED
        const slotCompletionsToProcess = [];

        // Process all flights
        let anyFlightUpdated = false;
        const updatedFlights = (prev.flights || []).map((flight) => {
          if (!flight) return flight;

          // =================================================================
          // BUG-3 FIX: Clean up stuck NaN flights
          // Only check for ACTUAL NaN (not undefined) and only for flights that
          // have been processed at least once (hasBeenProcessed = true)
          // New flights have undefined lat/lon which is fine - they get set later
          // =================================================================
          const isAirborne = ["takeoff", "climb", "cruise", "descent", "approach", "landing", "taxi_in"].includes(flight.phase);
          const hasNaNValues = Number.isNaN(flight.progress) || Number.isNaN(flight.distanceFraction) ||
                              Number.isNaN(flight.currentLat) || Number.isNaN(flight.currentLon);

          if (isAirborne && hasNaNValues && flight.hasBeenProcessed) {
            console.warn(`[NAN CLEANUP] Flight ${flight.flightNumber} has NaN values - marking as completed`);

            // Try to get destination coords, fall back to origin, fall back to hub
            const dest = getAirportData(flight.destination);
            const orig = getAirportData(flight.origin);
            const hubAirport = getAirportData(prev?.airline?.hub || 'KPDX');

            return {
              ...flight,
              phase: "completed",
              status: "completed",
              progress: 1.0,
              distanceFraction: 1.0,
              currentLat: dest?.lat ?? orig?.lat ?? hubAirport?.lat ?? 45.59,
              currentLon: dest?.lon ?? orig?.lon ?? hubAirport?.lon ?? -122.58,
              completedAt: newGameTime.toISOString(),
              gameLoopProcessed: true,
              nanCleanup: true, // Flag for debugging
            };
          }

          // =================================================================
          // AUTO-CANCELLATION REMOVED - Player must manually cancel flights
          // Flights will sit with increasing delay until player acts
          // =================================================================

          // Check for cancelled flights that need relations penalty
          if ((flight.status === "cancelled" || flight.phase === "cancelled") && !flight.relationsProcessed) {
            cancellationsToProcess.push(flight);
            return { ...flight, relationsProcessed: true };
          }

          // =================================================================
          // COMPLETED FLIGHTS - Process crew/rep/relations for marker-completed flights
          // When the visual marker calls onArrived, completeFlight() in WorldStateContext
          // marks the flight as "completed" and handles stats/cash. But we still need
          // to process crew duty, maintenance, reputation, and relations here.
          // =================================================================
          if ((flight.phase === "completed" || flight.status === "completed") && !flight.gameLoopProcessed) {
            // Flight was completed by marker callback - process crew/rep/relations
            const earnings = flight.preCalculatedEarnings || calculateEarnings(flight, prev);
            const delayed = (flight.totalDelayMinutes || 0) > 15;
            flightCompletions.push({ flight, earnings, delayed, skipCashProcessing: true });

            // Track slot completion to update slot status
            if (flight.slotId) {
              slotCompletionsToProcess.push({
                slotId: flight.slotId,
                airportCode: flight.origin,
                flightId: flight.id,
              });
            }

            return { ...flight, gameLoopProcessed: true };
          }

          // Skip already processed completed flights, cancelled, or emergency landed flights
          if ((flight.phase === "completed" && flight.gameLoopProcessed) ||
              flight.status === "cancelled" || flight.phase === "cancelled" ||
              flight.phase === "emergency_landed" || flight.status === "emergency_landed") {
            return flight;
          }

          // =================================================================
          // WEATHER HOLD REASSESSMENT - Clear holds if weather improves
          // This runs for all scheduled flights, not just at departure time
          // =================================================================
          if (flight.weatherHold && flight.phase === "scheduled") {
            const originWeather = prev.airportWeather?.[flight.origin];
            const weatherImpact = originWeather?.impact;

            // If weather has improved (canDepart is true or not grounded), clear the hold
            if (weatherImpact?.canDepart !== false) {
              debug(`[GameLoop] ${flight.flightNumber} weather improved, clearing hold`);
              anyFlightUpdated = true;
              return {
                ...flight,
                weatherHold: false,
                weatherReason: null,
              };
            }
          }

          // =================================================================
          // GROUND OPS - Phase transitions based on TIME UNTIL DEPARTURE
          // The SLOT DEPARTURE TIME is the anchor. Everything works backwards:
          //   - 15 min before: PREBOARDING
          //   - 8 min before: BOARDING
          //   - 2 min before: DOOR_CLOSED
          //   - AT departure: PUSHBACK
          //   - After pushback: TAXI_OUT
          //   - After taxi: TAKEOFF
          // =================================================================

          // Ground phase timing (minutes BEFORE scheduled departure)
          const PHASE_TRIGGERS = {
            preboarding: 15,  // 15 min before departure
            boarding: 8,      // 8 min before departure
            door_closed: 2,   // 2 min before departure
            pushback: 0,      // AT departure time
          };

          // Post-departure phase durations (minutes AFTER scheduled departure)
          const POST_DEPARTURE_DURATIONS = {
            pushback: 1.5,    // 1.5 min for pushback
            taxi_out: 2,      // 2 min taxi to runway
          };

          // For flights in post-departure phases (pushback, taxi), use elapsed time
          if (flight.phase === "pushback" && !flight.hasLaunched) {
            const phaseStart = flight.groundPhaseStartedAt ? new Date(flight.groundPhaseStartedAt).getTime() : newGameTime.getTime();
            const phaseElapsed = (newGameTime.getTime() - phaseStart) / 60000;

            if (phaseElapsed >= POST_DEPARTURE_DURATIONS.pushback) {
              debug(`[GameLoop] ${flight.flightNumber} pushback complete → taxi`);
              anyFlightUpdated = true;
              return { ...flight, phase: "taxi_out", groundPhaseStartedAt: newGameTime.toISOString() };
            }
            return flight;
          }

          if (flight.phase === "taxi_out" && !flight.hasLaunched) {
            const phaseStart = flight.groundPhaseStartedAt ? new Date(flight.groundPhaseStartedAt).getTime() : newGameTime.getTime();
            const phaseElapsed = (newGameTime.getTime() - phaseStart) / 60000;

            if (phaseElapsed >= POST_DEPARTURE_DURATIONS.taxi_out) {
              debug(`[GameLoop] ${flight.flightNumber} taxi complete → TAKEOFF`);
              soundManager.play(SOUNDS.FLIGHT_DEPART);

              // Calculate total delay (actual wheels-up vs scheduled departure)
              const scheduledDep = new Date(flight.scheduledDeparture).getTime();
              const actualTakeoff = newGameTime.getTime();
              // Expected takeoff is ~3.5 min after scheduled departure (pushback + taxi)
              const expectedTakeoffDelay = (POST_DEPARTURE_DURATIONS.pushback + POST_DEPARTURE_DURATIONS.taxi_out) * 60000;
              const lateDelayMinutes = Math.max(0, Math.floor((actualTakeoff - scheduledDep - expectedTakeoffDelay) / 60000));

              anyFlightUpdated = true;
              return {
                ...flight,
                phase: "takeoff",
                status: "in_flight",
                hasLaunched: true,
                phaseStartedAt: newGameTime.toISOString(),
                distanceTraveled: 0,
                distanceFraction: 0,
                progress: 0,
                totalDelayMinutes: lateDelayMinutes,
                delays: lateDelayMinutes > 0 ? [
                  ...(flight.delays || []),
                  { minutes: lateDelayMinutes, reason: 'Late departure', timestamp: newGameTime.toISOString(), type: 'late_departure' }
                ] : (flight.delays || []),
              };
            }
            return flight;
          }

          // SCHEDULED phase - transitions based on time UNTIL departure
          // Only trigger if preflight is complete
          if (flight.phase === "scheduled" && flight.preflightComplete && !flight.hasLaunched) {
            const scheduledDep = new Date(flight.scheduledDeparture).getTime();
            const currentTime = newGameTime.getTime();
            const minutesUntilDeparture = (scheduledDep - currentTime) / 60000;

            // Check weather
            const originWeather = prev.airportWeather?.[flight.origin];
            const weatherImpact = originWeather?.impact;

            // Weather ground stop
            if (weatherImpact?.canDepart === false) {
              if (!flight.weatherHold) {
                debug(`[GameLoop] ${flight.flightNumber} weather hold: ${weatherImpact.reason}`);
                anyFlightUpdated = true;
                return {
                  ...flight,
                  weatherHold: true,
                  weatherHoldStarted: newGameTime.toISOString(),
                  weatherReason: weatherImpact.reason,
                  delays: [
                    ...(flight.delays || []),
                    { minutes: 0, reason: weatherImpact.reason, timestamp: newGameTime.toISOString(), type: 'weather' }
                  ],
                };
              }

              // Check for auto-cancellation due to prolonged weather hold
              const holdStarted = flight.weatherHoldStarted ? new Date(flight.weatherHoldStarted) : null;
              if (holdStarted) {
                const holdDurationMs = currentTime - holdStarted.getTime();
                const holdDurationHours = holdDurationMs / (1000 * 60 * 60);

                // Auto-cancel if held for 4+ hours
                const cancellationCheck = checkAutoCancellation(flight, newGameTime);
                if (cancellationCheck.shouldCancel) {
                  debug(`[GameLoop] AUTO-CANCEL: ${flight.flightNumber} - ${cancellationCheck.reason}`);
                  const { cancelledFlight, costs } = processAutoCancellation(flight, cancellationCheck.reason, cancellationCheck.type);

                  autoCancellationsToProcess.push({
                    flight: cancelledFlight,
                    reason: cancellationCheck.reason,
                    type: cancellationCheck.type,
                    costs,
                  });

                  anyFlightUpdated = true;
                  return {
                    ...cancelledFlight,
                    phase: 'cancelled',
                    status: 'cancelled',
                  };
                }

                // Safety valve: If held for 2-4 hours, release anyway
                if (holdDurationHours >= 2) {
                  debug(`[GameLoop] ${flight.flightNumber} releasing from weather hold after ${holdDurationHours.toFixed(1)} hours`);
                  // Fall through to time-based transitions below
                } else {
                  return flight; // Stay grounded
                }
              } else {
                return flight; // Stay grounded
              }
            }

            // Clear weather hold if improved
            if (flight.weatherHold && weatherImpact?.canDepart !== false) {
              debug(`[GameLoop] ${flight.flightNumber} weather cleared`);
              anyFlightUpdated = true;
              return { ...flight, weatherHold: false, weatherReason: null };
            }

            // Transition to PREBOARDING at 15 min before
            if (minutesUntilDeparture <= PHASE_TRIGGERS.preboarding && minutesUntilDeparture > PHASE_TRIGGERS.boarding) {
              debug(`[GameLoop] ${flight.flightNumber} → PREBOARDING (${Math.round(minutesUntilDeparture)} min to departure)`);
              anyFlightUpdated = true;
              return { ...flight, phase: "preboarding", groundPhaseStartedAt: newGameTime.toISOString() };
            }

            // If we're past preboarding window but not yet at boarding, skip to boarding
            if (minutesUntilDeparture <= PHASE_TRIGGERS.boarding) {
              debug(`[GameLoop] ${flight.flightNumber} → BOARDING (${Math.round(minutesUntilDeparture)} min to departure)`);
              anyFlightUpdated = true;
              return { ...flight, phase: "boarding", groundPhaseStartedAt: newGameTime.toISOString() };
            }

            return flight;
          }

          // PREBOARDING phase - transition to BOARDING at 8 min before
          if (flight.phase === "preboarding" && !flight.hasLaunched) {
            const scheduledDep = new Date(flight.scheduledDeparture).getTime();
            const currentTime = newGameTime.getTime();
            const minutesUntilDeparture = (scheduledDep - currentTime) / 60000;

            if (minutesUntilDeparture <= PHASE_TRIGGERS.boarding) {
              debug(`[GameLoop] ${flight.flightNumber} → BOARDING`);
              anyFlightUpdated = true;
              return { ...flight, phase: "boarding", groundPhaseStartedAt: newGameTime.toISOString() };
            }
            return flight;
          }

          // BOARDING phase - transition to DOOR_CLOSED at 2 min before
          if (flight.phase === "boarding" && !flight.hasLaunched) {
            const scheduledDep = new Date(flight.scheduledDeparture).getTime();
            const currentTime = newGameTime.getTime();
            const minutesUntilDeparture = (scheduledDep - currentTime) / 60000;

            if (minutesUntilDeparture <= PHASE_TRIGGERS.door_closed) {
              debug(`[GameLoop] ${flight.flightNumber} → DOOR_CLOSED`);
              anyFlightUpdated = true;
              return { ...flight, phase: "door_closed", groundPhaseStartedAt: newGameTime.toISOString() };
            }
            return flight;
          }

          // DOOR_CLOSED phase - transition to PUSHBACK at departure time
          if (flight.phase === "door_closed" && !flight.hasLaunched) {
            const scheduledDep = new Date(flight.scheduledDeparture).getTime();
            const currentTime = newGameTime.getTime();
            const minutesUntilDeparture = (scheduledDep - currentTime) / 60000;

            if (minutesUntilDeparture <= PHASE_TRIGGERS.pushback) {
              debug(`[GameLoop] ${flight.flightNumber} → PUSHBACK (departure time reached)`);
              anyFlightUpdated = true;
              return { ...flight, phase: "pushback", groundPhaseStartedAt: newGameTime.toISOString() };
            }
            return flight;
          }

          // PENDING phase (preflight not complete) - nothing to do
          if (flight.phase === "pending" && !flight.hasLaunched) {
            return flight;
          }

          // Already launched but somehow back to scheduled - fix it
          if (flight.phase === "scheduled" && flight.hasLaunched) {
            console.warn(`[GameLoop] Flight ${flight.flightNumber} was reset to scheduled, fixing...`);
            return {
              ...flight,
              phase: "takeoff",
              status: "in_flight",
            };
          }

          // Ground phases are handled above - do NOT skip them

          // =================================================================
          // AIRBORNE PHASES - Progress based on DISTANCE, not time
          // Phase = pure function of (distanceTraveled / totalDistance)
          // =================================================================
          const isGroundPhase = ["pending", "scheduled", "preboarding", "boarding", "door_closed", "pushback", "taxi_out"].includes(flight.phase);
          const isAirbornePhase = ["takeoff", "climb", "cruise", "descent", "approach", "landing", "taxi_in", "arrived"].includes(flight.phase);
          if (isGroundPhase) {
            // Ground phases handled above
            return flight;
          }
          if (!isAirbornePhase) {
            // Unknown phase - don't touch it
            return flight;
          }

          // Get origin/destination airports (handles both object and array formats)
          const orig = getAirportData(flight.origin);
          const dest = getAirportData(flight.destination);
          if (!orig || !dest) {
            return flight; // Can't calculate without airports
          }

          // Calculate total route distance (nm)
          const totalDistance = calculateDistance(flight.origin, flight.destination);

          // =================================================================
          // BUG-3 FIX: Handle NaN and zero distance to prevent NaN poisoning
          // - NaN distance: invalid airports, mark flight for completion
          // - Zero distance: diversion back to origin (PDX->PDX), complete immediately
          // If distance is 0 or NaN, progress is 100% - flight is already there
          // =================================================================
          if (!Number.isFinite(totalDistance) || totalDistance <= 0) {
            console.warn(`[FLIGHT FIX] ${flight.flightNumber}: Invalid distance (${totalDistance}) for ${flight.origin}->${flight.destination}. Marking as arrived.`);

            // Complete the flight immediately - it's already at destination (or invalid)
            return {
              ...flight,
              phase: "arrived",
              status: "arrived",
              distanceTraveled: 0,
              distanceFraction: 1.0,
              progress: 1.0,
              currentLat: dest?.lat ?? orig?.lat ?? flight.currentLat,
              currentLon: dest?.lon ?? orig?.lon ?? flight.currentLon,
              completedAt: newGameTime.toISOString(),
              // Zero or negative revenue for invalid flights (diversions get refunded)
              revenue: totalDistance === 0 ? 0 : (flight.revenue || 0),
              invalidFlight: !Number.isFinite(totalDistance),
            };
          }

          // Get aircraft cruise speed (nm per hour)
          const aircraftData = AIRCRAFT_DATABASE[flight.aircraftType] || AIRCRAFT_DATABASE["E175"];
          const cruiseSpeedNmPerHour = aircraftData?.cruiseSpeed || 450;
          const cruiseSpeedNmPerMs = cruiseSpeedNmPerHour / (60 * 60 * 1000); // nm per millisecond

          // Initialize distanceTraveled if not set
          let distanceTraveled = flight.distanceTraveled || 0;

          // FIRST TICK FIX: Don't advance on the very first tick to prevent 2% offset
          // New flights should start exactly at origin, not jump ahead
          const isFirstTick = !flight.hasBeenProcessed;

          // Advance distance based on time elapsed (skip on first tick)
          // Uses flightDeltaMs (variable, controlled by speed slider)
          if (!isFirstTick) {
            // THE FOLD: Fixed duration flights ignore distance - all flights take same time
            if (flight.fixedDuration) {
              // fixedDuration is in GAME minutes
              // Progress = elapsed / total, where total = fixedDuration * 60 * 1000 ms
              const fixedDurationMs = flight.fixedDuration * 60 * 1000;
              const progressIncrement = flightDeltaMs / fixedDurationMs;
              // Convert to distance by multiplying by total distance
              distanceTraveled += totalDistance * progressIncrement;
            } else {
              distanceTraveled += cruiseSpeedNmPerMs * flightDeltaMs;
            }
          }
          // CLAMP: Never exceed total distance (prevents overshoot at high speeds)
          distanceTraveled = Math.min(distanceTraveled, totalDistance);

          // Calculate distance fraction with NaN guard (belt-and-suspenders)
          let distanceFraction = totalDistance > 0 ? (distanceTraveled / totalDistance) : 1;
          // Clamp to valid range and ensure no NaN
          distanceFraction = Number.isFinite(distanceFraction) ? Math.min(1, Math.max(0, distanceFraction)) : 1;

          // ============================================================================
          // EMERGENCY LANDING CHECK - Fuel exhaustion for range-exceeded flights
          // If player dispatched a flight beyond aircraft range, it runs out of fuel
          // ============================================================================
          if (flight.rangeExceeded && !flight.emergencyLanded) {
            // Calculate where fuel runs out (as fraction of total distance)
            const aircraftRange = flight.aircraftRange || 0;
            const fuelExhaustionFraction = aircraftRange / totalDistance;

            // Check if we've reached fuel exhaustion point
            if (distanceFraction >= fuelExhaustionFraction) {
              // Calculate current position using great-circle interpolation
              const emergencyT = Math.min(1, Math.max(0, distanceFraction));
              const emergencyPos = intermediatePoint(orig.lat, orig.lon, dest.lat, dest.lon, emergencyT);
              const currentLat = emergencyPos.lat;
              const currentLon = emergencyPos.lon;

              // Calculate consequences (includes nearest airport and severity)
              const consequences = calculateEmergencyLandingConsequences(flight, currentLat, currentLon, prev);

              debug(`[EMERGENCY LANDING] ${flight.flightNumber} ran out of fuel at ${(distanceFraction * 100).toFixed(1)}% - ${consequences.severity.toUpperCase()}`);
              emergencyLandingsToProcess.push({ flight, consequences });
              anyFlightUpdated = true;

              return {
                ...flight,
                phase: "emergency_landed",
                status: "emergency_landed",
                emergencyLanded: true,
                emergencyLandingConsequences: consequences,
                currentLat,
                currentLon,
                completedAt: newGameTime.toISOString(),
                revenue: 0,
                costs: consequences.totalCost,
                profit: -consequences.totalCost,
              };
            }
          }

          // ============================================================================
          // FLIGHT ARRIVAL - When distance >= 100%, mark as "arrived" but NOT "completed"
          //
          // CRITICAL: Flights should ONLY be marked "completed" when the visual marker
          // reports arrival via onArrived callback. This prevents the marker from
          // disappearing mid-flight due to calculation timing differences.
          //
          // The marker in WorldMap.jsx has its own animation and calls onArrived
          // when it visually reaches the destination. That callback triggers
          // completeFlight() in WorldStateContext, which then marks the flight
          // as "completed" and processes earnings/stats.
          //
          // GameLoopEngine handles crew/maintenance/reputation/relations processing
          // AFTER the flight is marked "completed" by the marker callback.
          // ============================================================================

          // THE PERPETUAL: Circumnavigates equator for 3 days
          if (flight.neverAutoComplete && distanceFraction >= 1.0) {
            const startDay = flight.perpetualStartDay || 1;
            const currentDay = prev.dayNumber || 1;
            const daysFlying = currentDay - startDay;
            const maxDays = 3;

            // Check if 3 days have passed - time to retire
            if (daysFlying >= maxDays) {
              debug(`[GameLoop] The Perpetual retiring after ${daysFlying} days`);
              anyFlightUpdated = true;
              return {
                ...flight,
                neverAutoComplete: false, // Allow completion
                forceRetire: true,
              };
            }

            // Move to next equator waypoint
            const waypoints = flight.equatorWaypoints || ["SEQM", "DNMM", "VRMM", "WIII"];
            const currentIdx = flight.currentWaypointIndex || 0;
            const nextIdx = (currentIdx + 1) % waypoints.length;
            const nextNextIdx = (nextIdx + 1) % waypoints.length;

            // Get new origin coordinates to prevent visual jump
            const newOrigin = getAirportData(waypoints[nextIdx]);

            debug(`[GameLoop] The Perpetual: ${waypoints[nextIdx]} → ${waypoints[nextNextIdx]} (day ${daysFlying + 1}/${maxDays})`);

            anyFlightUpdated = true;
            return {
              ...flight,
              origin: waypoints[nextIdx],
              destination: waypoints[nextNextIdx],
              currentWaypointIndex: nextIdx,
              distanceTraveled: 0,
              distanceFraction: 0,
              progress: 0,
              perpetualLoops: (flight.perpetualLoops || 0) + 1,
              // Set position to new origin to prevent visual jump
              currentLat: newOrigin?.lat ?? flight.currentLat,
              currentLon: newOrigin?.lon ?? flight.currentLon,
            };
          }

          // ECHO FLIGHT: Auto-redispatch on completion (up to 10 cycles)
          if (flight.autoRedispatch && distanceFraction >= 1.0) {
            const currentCycles = flight.echoCycles || 0;
            const maxCycles = 10;

            if (currentCycles < maxCycles - 1) {
              // Reset for another cycle - swap origin/destination for return trip
              // New origin is old destination, so use dest coords to prevent visual jump
              anyFlightUpdated = true;
              debug(`[GameLoop] Echo Flight cycle ${currentCycles + 1}/${maxCycles} - auto-redispatching`);
              return {
                ...flight,
                origin: flight.destination,
                destination: flight.origin,
                distanceTraveled: 0,
                distanceFraction: 0,
                progress: 0,
                echoCycles: currentCycles + 1,
                phase: "cruise",
                status: "in_flight",
                // Position stays at destination (now the new origin)
                currentLat: dest.lat,
                currentLon: dest.lon,
              };
            } else {
              // Final cycle - let it complete and retire
              debug(`[GameLoop] Echo Flight final cycle - retiring to museum`);
              // Remove autoRedispatch so it completes normally
              anyFlightUpdated = true;
              return {
                ...flight,
                autoRedispatch: false,
                echoCycles: maxCycles,
                // Will be marked for museum by anomalous behavior on complete
              };
            }
          }

          if (distanceFraction >= 1.0 && !flight.awaitingVisualArrival) {
            // Flight has reached destination by calculation - await visual arrival
            // DO NOT mark as "completed" - that's the marker's job via onArrived callback
            anyFlightUpdated = true;
            return {
              ...flight,
              phase: "arrived",
              status: "arrived",
              distanceTraveled: totalDistance,
              distanceFraction: 1.0,
              progress: 1.0,
              currentLat: dest.lat,
              currentLon: dest.lon,
              arrivedAt: newGameTime.toISOString(),
              awaitingVisualArrival: true, // Flag to prevent re-processing
              // Use existing preCalculatedEarnings (from Simple mode) or calculate
              preCalculatedEarnings: flight.preCalculatedEarnings || calculateEarnings(flight, prev),
            };
          }

          // Flight already at destination, waiting for visual marker callback
          if (flight.awaitingVisualArrival && distanceFraction >= 1.0) {
            // AUTO-COMPLETE FAILSAFE: If awaiting visual arrival for too long (2+ seconds game time),
            // auto-complete the flight. This handles cases where the map isn't visible (user on Routes page)
            // or the marker failed to trigger onArrived callback.
            const arrivedAt = flight.arrivedAt ? new Date(flight.arrivedAt) : null;
            const waitingMs = arrivedAt ? (newGameTime.getTime() - arrivedAt.getTime()) : 0;
            const waitingSeconds = waitingMs / 1000;

            // Track tick count for this flight (initialized on first tick, not in initial data)
            const tickCount = (flight.awaitingArrivalTicks || 0) + 1;

            // Auto-complete if:
            // - 2+ seconds game time have passed since arrival, OR
            // - 4+ ticks have occurred (fallback for when game time isn't advancing), OR
            // - arrivedAt is missing AND we've been through 2+ ticks (handles edge case)
            const shouldAutoComplete = waitingSeconds > 2 || tickCount > 4 || (!arrivedAt && tickCount > 2);

            if (shouldAutoComplete) {
              // Auto-complete this flight - it's been waiting too long
              debug(`[GameLoop] Auto-completing stuck flight ${flight.id} (waited ${waitingSeconds.toFixed(1)}s game time)`);
              anyFlightUpdated = true;

              // Process earnings
              const earnings = flight.preCalculatedEarnings || calculateEarnings(flight, prev);
              const delayed = (flight.totalDelayMinutes || 0) > 15;
              flightCompletions.push({ flight, earnings, delayed, skipCashProcessing: false });

              return {
                ...flight,
                phase: "completed",
                status: "completed",
                completedAt: newGameTime.toISOString(),
                currentLat: dest.lat,
                currentLon: dest.lon,
                gameLoopProcessed: true,
                // Add financial data so Financials page can see this flight
                revenue: earnings.revenue,
                costs: earnings.costs,
                profit: earnings.profit,
              };
            }

            // Keep position at destination, increment tick count for tracking
            return {
              ...flight,
              currentLat: dest.lat,
              currentLon: dest.lon,
              awaitingArrivalTicks: tickCount,
            };
          }


          // =================================================================
          // IN-FLIGHT: Derive phase from distance fraction
          // =================================================================
          const newPhase = getPhaseFromDistanceFraction(distanceFraction, totalDistance);

          let updated = {
            ...flight,
            distanceTraveled,
            distanceFraction,
            progress: distanceFraction,
            hasBeenProcessed: true, // Mark as processed to fix first-tick offset
          };

          // Handle phase change
          const oldPhase = flight.phase;
          if (newPhase !== oldPhase) {
            anyFlightUpdated = true;
            updated.phase = newPhase;
            updated.phaseStartedAt = newGameTime.toISOString();
          }

          // Calculate position for map display using great-circle interpolation
          const t = Math.min(1, Math.max(0, distanceFraction));
          const { lat, lon } = intermediatePoint(orig.lat, orig.lon, dest.lat, dest.lon, t);
          updated.currentLat = lat;
          updated.currentLon = lon;

          // =================================================================
          // IN-FLIGHT DIVERSION CHECK - Check destination weather
          // =================================================================
          // Only check during cruise phase (when most diversions happen)
          if (newPhase === 'cruise' && !flight.diverted) {
            const destWeather = prev.airportWeather?.[flight.destination]?.impact;
            const distanceRemaining = totalDistance - distanceTraveled;

            const diversionCheck = shouldDivert(flight, destWeather, distanceRemaining);

            if (diversionCheck.shouldDivert) {
              // Find alternate airport (excludes origin to prevent origin===destination crash)
              const alternate = findAlternateAirport(
                lat,
                lon,
                flight.destination,
                prev.airportWeather || {},
                flight.aircraftType,
                flight.origin  // Exclude origin - diverting back would cause crash
              );

              if (alternate) {
                // Check if we can safely divert (fuel)
                const fuelCheck = canSafelyDivert(flight, alternate);

                if (fuelCheck.canDivert) {
                  // Initiate diversion
                  const divertedFlight = initiateDiversion(flight, alternate, diversionCheck.reason);
                  debug(`[GameLoop] DIVERSION: ${flight.flightNumber} diverting to ${alternate.icao} - ${diversionCheck.reason}`);

                  // Track for cost processing after map completes
                  diversionsToProcess.push({
                    flight: divertedFlight,
                    alternate,
                    reason: diversionCheck.reason,
                    costs: divertedFlight.diversionRecord?.costs,
                  });

                  anyFlightUpdated = true;

                  return {
                    ...updated,
                    ...divertedFlight,
                    currentLat: lat,
                    currentLon: lon,
                  };
                } else {
                  // Can't divert safely - mark for potential emergency
                  updated.noDiversionAvailable = true;
                  updated.fuelEmergencyReason = fuelCheck.reason;
                }
              } else {
                // No alternate found
                updated.noDiversionAvailable = true;
              }
            }
          }

          return updated;
        });

        // Update day number based on game start date (real-time start)
        const startDate = new Date(prev.gameStartDate || prev.gameTime);
        const dayNumber = Math.floor((newGameTime - startDate) / (24 * 60 * 60 * 1000)) + 1;

        // ===============================================================
        // ZOMBIE NEWS - Check for new outbreak news when day changes
        // ===============================================================
        let zombieNewsItems = [];
        if (prev.zombieOutbreakStarted && dayNumber !== prev.dayNumber) {
          const currentDay = dayNumber;
          const outbreakStartDay = prev.zombieOutbreakDay || 1;
          const daysSinceOutbreak = currentDay - outbreakStartDay;
          const newsSequence = prev.zombieNewsSequence || [];

          // Check each news item that should trigger today
          for (const item of newsSequence) {
            if (item.day <= daysSinceOutbreak && !item.shown) {
              zombieNewsItems.push({
                severity: 'zombie',
                headline: item.text,
                message: item.text,
              });
              debug(`[GameLoop] Zombie news Day ${daysSinceOutbreak}: ${item.text}`);
            }
          }
        }

        // ===============================================================
        // WEATHER - Now uses live real-time weather from Tomorrow.io
        // ===============================================================
        // Weather is fetched on real-world time intervals (not game time)
        // via WorldStateContext.jsx and WeatherService.js. The airportWeather
        // state is updated independently of the game loop.
        // Flight departure weather checks use prev.airportWeather below.

        // ===============================================================
        // SYSTEMS TICK - Macro, Progression, Crew, Monthly billing
        // ===============================================================
        let systemsUpdates = {};
        let systemsNews = [];
        try {
          const result = tickAllSystems(prev, newGameTime.toISOString());
          systemsUpdates = result.updates || {};
          systemsNews = result.news || [];
        } catch (e) {
          console.warn("Systems tick failed:", e);
        }

        // Add zombie news to systems news
        if (zombieNewsItems.length > 0) {
          systemsNews = [...zombieNewsItems, ...systemsNews];
        }

        // ===============================================================
        // END OF DAY DETECTION
        // ===============================================================
        // Check if it's late night (10 PM - 6 AM) and no flights are active or scheduled
        const gameHour = newGameTime.getUTCHours();
        const isLateNight = gameHour >= 22 || gameHour < 6; // 10 PM to 6 AM UTC

        // Check for active or remaining flights today
        const hasActiveFlights = updatedFlights.some(f =>
          f.phase === 'in_flight' ||
          f.phase === 'boarding' ||
          f.phase === 'taxi_out' ||
          f.phase === 'takeoff' ||
          f.phase === 'cruise' ||
          f.phase === 'descent' ||
          f.phase === 'approach' ||
          f.phase === 'taxi_in'
        );

        const hasScheduledFlightsToday = updatedFlights.some(f => {
          if (f.phase !== 'scheduled') return false;
          const depTime = new Date(f.scheduledDeparture);
          // Same day check (compare dates)
          return depTime.toDateString() === newGameTime.toDateString();
        });

        // End of day auto-pause: Only in Simulation mode (Simple mode lets users play continuously)
        const isSimpleMode = prev.gameMode === 'simple' || prev.gameMode === 'arcade';
        const isEndOfDay = !isSimpleMode && gameHour >= 22 && !prev.endOfDayHandled && !prev.pendingEndOfDay;

        // Mark zombie news items as shown
        let updatedZombieSequence = prev.zombieNewsSequence;
        if (zombieNewsItems.length > 0 && prev.zombieNewsSequence) {
          const currentDay = dayNumber;
          const outbreakStartDay = prev.zombieOutbreakDay || 1;
          const daysSinceOutbreak = currentDay - outbreakStartDay;
          updatedZombieSequence = prev.zombieNewsSequence.map(item =>
            item.day <= daysSinceOutbreak ? { ...item, shown: true } : item
          );
        }

        // Build updated state
        const newState = {
          ...prev,
          ...systemsUpdates, // Apply systems updates (macro, progression, crew, stats, etc.)
          gameTime: newGameTime.toISOString(),
          dayNumber: dayNumber > 0 ? dayNumber : 1,
          flights: updatedFlights,
          // Weather state is managed by live weather system (airportWeather via WorldStateContext)
          // Merge systems news into newsFeed - deduplicate by text
          newsFeed: deduplicateNews(systemsNews, prev.newsFeed || []),
          // Update zombie news sequence if any news was shown
          ...(updatedZombieSequence !== prev.zombieNewsSequence ? {
            zombieNewsSequence: updatedZombieSequence,
          } : {}),
          // End of day trigger - pause game and show modal
          ...(isEndOfDay ? {
            pendingEndOfDay: true,
            endOfDayHandled: true,
            isPaused: true, // Pause the game
          } : {}),
          // Apply buff expiration updates (if any)
          ...(buffUpdates ? { buffs: buffUpdates } : {}),
        };
        
        // ===============================================================
        // PROCESS FLIGHT COMPLETIONS (crew hours, stats, reputation, relations)
        // Uses ReputationEngine.js scale: 0-15000+ (not 0-100)
        // ===============================================================
        if (flightCompletions.length > 0) {
          let crewState = newState.crewDuty || prev.crewDuty || createCrewDutyState();
          let maintenanceState = newState.maintenance || prev.maintenance || createMaintenanceState();
          let repState = newState.reputation || prev.reputation || createReputationState();
          let relationsState = newState.relations || prev.relations || createRelationsState();
          let stats = { ...(newState.stats || prev.stats || {}) };
          const crewNews = [];

          // CRITICAL: Update aircraft status for completed flights
          // This fixes the bug where aircraft stay stuck as 'in_flight' after auto-complete
          let updatedAircraft = [...(newState.aircraft || prev.aircraft || [])];

          // Track tier before updates for "Tier Up!" detection
          const oldTier = calculateTier(prev.reputation?.currentScore || 0);

          for (const { flight, earnings, delayed, skipCashProcessing } of flightCompletions) {
            // ===============================================================
            // UPDATE AIRCRAFT STATUS - Mark as available at destination
            // ===============================================================
            if (flight.aircraftId) {
              const acIndex = updatedAircraft.findIndex(a => a?.id === flight.aircraftId);
              if (acIndex >= 0) {
                const ac = updatedAircraft[acIndex];
                debug(`[GameLoop] Aircraft ${ac.registration || ac.id} completed flight, now available at ${flight.destination}`);
                updatedAircraft[acIndex] = {
                  ...ac,
                  status: 'available',
                  location: flight.destination,
                  currentFlightId: null,
                };
              }
            }
            // Play landing sound - profitable flights get the money ding
            if (earnings.profit > 0) {
              soundManager.play(SOUNDS.MONEY_LAND);
            } else {
              soundManager.play(SOUNDS.FLIGHT_LAND);
            }

            // Update crew hours
            let { crewState: updatedCrew, warnings, isIllegal } = updateCrewForFlight(crewState, {
              ...flight,
              blockHours: earnings.blockHours,
            }, prev);
            crewState = updatedCrew;
            crewNews.push(...warnings);

            // TECH SYSTEM: CrewWise can prevent crew legality busts
            if (isIllegal) {
              const preventionChance = getCrewBustPreventionChance(prev.tech);
              if (preventionChance > 0 && Math.random() < preventionChance) {
                isIllegal = false; // CrewWise saved us!
                crewNews.push({
                  severity: 'success',
                  message: `CrewWise prevented a crew legality violation on ${flight.flightNumber}`,
                });
              }
            }

            // TECH SYSTEM: YieldMaster boosts revenue (only if we're processing cash)
            if (!skipCashProcessing) {
              const revenueBoost = getRevenueBoost(prev.tech);
              const boostedRevenue = Math.round(earnings.revenue * (1 + revenueBoost));
              earnings.revenue = boostedRevenue;
            }

            // DIVERSION PENALTY: Diverted flights only earn 1/4 revenue
            // Passengers didn't reach their destination - partial refunds required
            if (flight.diverted) {
              const diversionPenalty = 0.25;
              earnings.revenue = Math.round(earnings.revenue * diversionPenalty);
              earnings.profit = earnings.revenue - earnings.costs;
              debug(`[GameLoop] DIVERSION PENALTY: ${flight.flightNumber} revenue reduced to 25% ($${earnings.revenue})`);
            }

            // Update maintenance hours
            maintenanceState = updateMaintenanceHours(maintenanceState, flight.aircraftId, earnings.blockHours, newGameTime.toISOString());

            // ===============================================================
            // PASSENGER SATISFACTION - Calculate and store on flight
            // ===============================================================
            const originWeather = prev.airportWeather?.[flight.origin]?.impact || null;
            const destWeather = prev.airportWeather?.[flight.destination]?.impact || null;
            const crewSkill = getCrewSkillLevel(flight.crewId, prev.crew || []);

            const satisfactionResult = calculateFlightSatisfaction(
              flight,
              flight.delays || [],
              originWeather,
              destWeather,
              crewSkill
            );

            // Store satisfaction on flight for records
            flight.passengerSatisfaction = satisfactionResult.satisfaction;
            flight.satisfactionFactors = satisfactionResult.factors;

            // Update reputation using ReputationEngine (0-15000+ scale)
            // Record events for daily tracking AND update currentScore immediately for feedback
            const delayMinutes = flight.totalDelayMinutes || 0;
            if (delayMinutes <= 14) {
              // On-time arrival: +35 points
              repState = recordReputationEvent(repState, 'ON_TIME_ARRIVAL', { flightId: flight.id, delay: delayMinutes });
              repState.currentScore = Math.max(0, (repState.currentScore || 0) + REPUTATION_POINTS.ON_TIME_ARRIVAL);
            } else if (delayMinutes >= 90) {
              // Major delay: -40 points
              repState = recordReputationEvent(repState, 'MAJOR_DELAY_90', { flightId: flight.id, delay: delayMinutes });
              repState.currentScore = Math.max(0, (repState.currentScore || 0) + REPUTATION_POINTS.MAJOR_DELAY_90);
            } else if (delayMinutes >= 30) {
              // Minor delay: -15 points
              repState = recordReputationEvent(repState, 'MINOR_DELAY_30', { flightId: flight.id, delay: delayMinutes });
              repState.currentScore = Math.max(0, (repState.currentScore || 0) + REPUTATION_POINTS.MINOR_DELAY_30);
            }
            if (isIllegal) {
              // Crew legality bust: -60 points
              repState = recordReputationEvent(repState, 'CREW_LEGALITY_BUST', { flightId: flight.id });
              repState.currentScore = Math.max(0, (repState.currentScore || 0) + REPUTATION_POINTS.CREW_LEGALITY_BUST);
            }

            // Passenger satisfaction bonus (if >= 80%)
            if (satisfactionResult.meetsReputationThreshold) {
              repState = recordReputationEvent(repState, 'PAX_SATISFACTION_80', {
                flightId: flight.id,
                satisfaction: satisfactionResult.satisfaction,
              });
              repState.currentScore = Math.max(0, (repState.currentScore || 0) + (REPUTATION_POINTS.PAX_SATISFACTION_80 || 15));
            }

            // ===============================================================
            // UPDATE AIRPORT RELATIONS - Every flight touches both endpoints
            // ===============================================================
            const dest = flight.destination;
            const origin = flight.origin;
            const delayMins = flight.totalDelayMinutes || 0;

            // DESTINATION AIRPORT RELATIONS
            if (delayMins < 15) {
              // On-time arrival: +0.5 loyalty
              relationsState = airportShiftsLoyalty(relationsState, dest, 'PLAYER', 0.5);
            }
            if (delayMins >= 90) {
              // Major delay: -2 loyalty
              relationsState = airportShiftsLoyalty(relationsState, dest, 'PLAYER', -2);
            } else if (delayMins >= 30) {
              // Minor delay: -1 loyalty
              relationsState = airportShiftsLoyalty(relationsState, dest, 'PLAYER', -1);
            }
            if (isIllegal) {
              // Crew legality bust at destination: -5 loyalty
              relationsState = airportShiftsLoyalty(relationsState, dest, 'PLAYER', -5);
            }

            // ORIGIN AIRPORT RELATIONS (they care about departures)
            if (delayMins < 15) {
              // On-time departure: +0.3 loyalty
              relationsState = airportShiftsLoyalty(relationsState, origin, 'PLAYER', 0.3);
            }

            // ===============================================================
            // SIMPLE MODE HUB INVITE CHECK
            // Hub invites require: cash >= hub base value, 5+ relations, on-time flight
            // Max 1 invite per game day - extras go to queue
            // ===============================================================
            const isSimpleMode = prev.gameMode === 'simple';
            if (isSimpleMode && delayMins < 15) {
              // Record the player relations event (for future use)
              relationsState = recordAirportRelationsEvent(relationsState, dest, 'ON_TIME_FLIGHT', { flightId: flight.id });

              // Check if player has enough CASH to afford this hub
              const currentCash = (newState.airline?.cash ?? prev.airline?.cash) || 0;
              const hubBaseValue = getHubBaseValue(dest);

              if (currentCash >= hubBaseValue) {
                const existingHubs = prev.airline?.hubs || ['KPDX'];
                const pendingInvites = newState.pendingHubInvites || prev.pendingHubInvites || [];
                const queuedInvites = newState.queuedHubInvites || prev.queuedHubInvites || [];
                const completedAuctions = prev.airline?.completedHubAuctions || [];
                const failedAuctions = prev.airline?.failedHubAuctions || [];
                const lastInviteDay = newState.lastHubInviteDay ?? prev.lastHubInviteDay ?? 0;
                const currentDay = prev.dayNumber || 1;

                // Check if we should trigger an invite for this destination
                const alreadyHasHub = existingHubs.includes(dest);
                const alreadyInvited = pendingInvites.includes(dest);
                const alreadyQueued = queuedInvites.includes(dest);
                const alreadyCompleted = completedAuctions.includes(dest);
                // Allow retry after 7 days (in game time) - uses gameTime stored in failed auction
                const gameNow = newGameTime.getTime();
                const recentlyFailed = failedAuctions.some(f =>
                  f.hub === dest && (gameNow - (f.gameTimestamp || f.timestamp)) < 7 * 24 * 60 * 60 * 1000
                );

                // Need some minimum activity at the airport (5+ on-time flights)
                const destRelations = relationsState?.playerRelations?.airports?.[dest]?.current || 0;
                const MIN_RELATIONS = 5;

                if (destRelations >= MIN_RELATIONS &&
                    !alreadyHasHub &&
                    !alreadyInvited &&
                    !alreadyQueued &&
                    !alreadyCompleted &&
                    !recentlyFailed) {

                  // Check if we already sent an invite today (max 1 per day)
                  const alreadyInvitedToday = lastInviteDay === currentDay;

                  if (alreadyInvitedToday) {
                    // Queue for later
                    debug(`[GameLoop] HUB QUEUED: ${dest} - already sent invite today, queuing for tomorrow`);
                    if (!newState.queuedHubInvites) {
                      newState.queuedHubInvites = [...queuedInvites];
                    }
                    newState.queuedHubInvites.push(dest);
                  } else {
                    // Trigger hub invite!
                    debug(`[GameLoop] HUB INVITE: ${dest} - player has $${(currentCash/1000000).toFixed(1)}M cash (needs $${(hubBaseValue/1000000).toFixed(1)}M), relations: ${destRelations}`);
                    if (!newState.pendingHubInvites) {
                      newState.pendingHubInvites = [...pendingInvites];
                    }
                    newState.pendingHubInvites.push(dest);
                    newState.lastHubInviteDay = currentDay;
                  }
                }
              }
            }

            // Skip stats/cash updates for marker-completed flights
            // (completeFlight in WorldStateContext already handled those)
            if (!skipCashProcessing) {
              // Update stats
              debug('%c✈️ FLIGHT COMPLETED (GameLoopEngine auto-complete) ✈️', 'color: #ff9900; font-weight: bold; font-size: 12px;');
              debug(`%c  Flight: ${flight.flightNumber || flight.id}`, 'color: #ff9900;');
              debug(`%c  dailyFlights BEFORE: ${stats.dailyFlights || 0}`, 'color: #ffff00;');
              debug(`%c  dailyFlights AFTER:  ${(stats.dailyFlights || 0) + 1}`, 'color: #00ffff; font-weight: bold;');
              stats.completedFlights = (stats.completedFlights || 0) + 1;
              stats.dailyFlights = (stats.dailyFlights || 0) + 1;
              stats.monthlyFlights = (stats.monthlyFlights || 0) + 1;

              // Track widebody flights for fleet progression unlock
              const aircraftData = AIRCRAFT_DATABASE[flight.aircraftType];
              const category = aircraftData?.category?.toLowerCase() || '';
              if (category === 'widebody' || category === 'heavy' || category === 'superheavy') {
                stats.widebodyFlights = (stats.widebodyFlights || 0) + 1;
              }
              stats.totalRevenue = (stats.totalRevenue || 0) + earnings.revenue;
              stats.dailyRevenue = (stats.dailyRevenue || 0) + earnings.revenue;
              stats.monthlyRevenue = (stats.monthlyRevenue || 0) + earnings.revenue;
              stats.totalCosts = (stats.totalCosts || 0) + earnings.costs;
              stats.dailyCosts = (stats.dailyCosts || 0) + earnings.costs;
              stats.totalProfit = (stats.totalProfit || 0) + earnings.profit;
              stats.monthlyFuelBurn = (stats.monthlyFuelBurn || 0) + (earnings.blockHours * 2400); // ~2400 kg/hr for E175

              // ADD PROFIT TO CASH! This is the critical line that was missing.
              cashChange += earnings.profit;

              if (delayed) {
                stats.dailyDelays = (stats.dailyDelays || 0) + 1;
              } else {
                // Track on-time flights for OTP calculation
                stats.onTimeFlights = (stats.onTimeFlights || 0) + 1;
              }
            } else {
              // For marker-completed flights, just track fuel burn (not duplicated in completeFlight)
              stats.monthlyFuelBurn = (stats.monthlyFuelBurn || 0) + (earnings.blockHours * 2400);

              if (delayed) {
                stats.dailyDelays = (stats.dailyDelays || 0) + 1;
              } else {
                stats.onTimeFlights = (stats.onTimeFlights || 0) + 1;
              }
            }
          }

          newState.crewDuty = crewState;
          newState.maintenance = maintenanceState;
          newState.reputation = repState;
          newState.relations = relationsState;
          newState.stats = stats;

          // ===============================================================
          // FUEL USAGE TRACKING (Simulation Mode only)
          // Record contract/hedge usage for completed flights
          // ===============================================================
          if (prev.gameMode === 'simulation' && prev.fuel && flightCompletions.length > 0) {
            let updatedFuelState = newState.fuel || prev.fuel;
            for (const { flight, earnings } of flightCompletions) {
              if (earnings.breakdown?.fuelGallons && earnings.breakdown.fuelSource) {
                const fuelInfo = getEffectiveFuelPrice(updatedFuelState, flight.origin, earnings.breakdown.fuelGallons);
                updatedFuelState = recordFuelUsage(updatedFuelState, earnings.breakdown.fuelGallons, fuelInfo);
              }
            }
            newState.fuel = updatedFuelState;
          }

          // ===============================================================
          // ROUTE DEMAND DEPLETION
          // Deplete demand for each completed flight's route
          // ===============================================================
          if (flightCompletions.length > 0) {
            const updatedRouteDemand = { ...(newState.routeDemand || prev.routeDemand || {}) };
            for (const { flight } of flightCompletions) {
              if (flight.origin && flight.destination) {
                const newDemand = depleteRoute(flight.origin, flight.destination, updatedRouteDemand);
                debug(`[GameLoop] Route demand: ${flight.origin}-${flight.destination} → ${newDemand.toFixed(1)}%`);
              }
            }
            newState.routeDemand = updatedRouteDemand;
          }

          // ===============================================================
          // TIER UP DETECTION - Compare computed tiers (not stored)
          // Tier = calculateTier(reputationScore) - always derived
          // ===============================================================
          const newTier = calculateTier(repState.currentScore || 0);
          if (newTier > oldTier) {
            const tierInfo = TIER_THRESHOLDS[newTier];
            soundManager.play(SOUNDS.TIER_UP);
            crewNews.push({
              type: 'success',
              text: `Tier ${newTier} reached: ${tierInfo?.name || 'New Tier'}. New airports available.`,
            });
          }

          // Add crew warnings to news (deduplicated) - SKIP in Simple Mode (no crew system)
          const isSimpleModeForCrew = prev.gameMode === 'simple' || prev.gameMode === 'arcade';
          if (crewNews.length > 0 && !isSimpleModeForCrew) {
            newState.newsFeed = deduplicateNews(crewNews.slice(0, 2), newState.newsFeed);
          }

          // CRITICAL: Apply aircraft status updates (available at destination)
          newState.aircraft = updatedAircraft;

          // ===============================================================
          // SIMPLE MODE: ROUND-TRIP ROUTE HANDLING
          // When a flight completes:
          // 1. Enter "landed" phase (3 real sec = ~7 game min at 144x)
          // 2. Flip route direction for return leg
          // 3. Start preboarding countdown (15 real sec = ~36 game min)
          // ===============================================================
          if (prev.gameMode === 'simple' || prev.gameMode === 'arcade') {
            const LANDING_BEAT_MINUTES = 7;    // ~3 real seconds at 144x clock
            const PREBOARD_MINUTES = 36;       // ~15 real seconds at 144x clock
            const TURNAROUND_MINUTES = LANDING_BEAT_MINUTES + PREBOARD_MINUTES; // Total ~43 game min
            const routeScheduleUpdates = new Map();

            for (const { flight } of flightCompletions) {
              if (flight.routeId) {
                // Find the route to flip direction
                const currentRoutes = newState.routes || prev.routes || [];
                const route = currentRoutes.find(r => r.id === flight.routeId);

                // ROUND-TRIP: Flip the route direction
                // Aircraft landed at flight.destination, next leg goes back to flight.origin
                const newLegOrigin = flight.destination;
                const newLegDestination = flight.origin;

                // SIMPLE MODE: Don't set scheduledDeparture since player manually clicks "Fly"
                // This prevents the auto-cancel system from penalizing idle routes
                routeScheduleUpdates.set(flight.routeId, {
                  scheduledDeparture: null,  // Player will manually dispatch
                  groundPhase: null,
                  groundPhaseStartedAt: null,
                  dispatchedAt: null,
                  status: 'landed',  // New status: landed beat before preboard
                  landedAt: newGameTime.toISOString(),
                  lastCompletedAt: newGameTime.toISOString(),
                  // ROUND-TRIP: Flip direction for return leg
                  currentLegOrigin: newLegOrigin,
                  currentLegDestination: newLegDestination,
                });
                // DEBUG: Log route flip details
                debug(`[ROUTE FLIP] Flight ${flight.id} completed:`, {
                  flightOrigin: flight.origin,
                  flightDestination: flight.destination,
                  newLegOrigin,
                  newLegDestination,
                  routeId: flight.routeId,
                  routeBeforeFlip: route ? {
                    origin: route.origin,
                    destination: route.destination,
                    currentLegOrigin: route.currentLegOrigin,
                    currentLegDestination: route.currentLegDestination,
                  } : 'NOT FOUND',
                });
              }
            }

            // Apply route updates
            if (routeScheduleUpdates.size > 0) {
              const currentRoutes = newState.routes || prev.routes || [];
              newState.routes = currentRoutes.map(r => {
                const updates = routeScheduleUpdates.get(r.id);
                return updates ? { ...r, ...updates } : r;
              });
            }
          }
        }

        // ===============================================================
        // PROCESS CANCELLATIONS - Apply relations penalties
        // ===============================================================
        if (cancellationsToProcess.length > 0) {
          let relationsState = newState.relations || prev.relations || createRelationsState();

          for (const flight of cancellationsToProcess) {
            const dest = flight.destination;
            const origin = flight.origin;

            // Cancelled flight: -5 at destination
            if (dest) {
              relationsState = airportShiftsLoyalty(relationsState, dest, 'PLAYER', -5);
            }

            // Cancelled flight: -5 at origin (they had a gate ready)
            if (origin) {
              relationsState = airportShiftsLoyalty(relationsState, origin, 'PLAYER', -5);
            }
          }

          newState.relations = relationsState;
        }

        // ===============================================================
        // PROCESS EMERGENCY LANDINGS - Apply severe consequences
        // ===============================================================
        if (emergencyLandingsToProcess.length > 0) {
          let relationsState = newState.relations || prev.relations || createRelationsState();
          let repState = newState.reputation || prev.reputation || createReputationState();
          let eosState = newState.eos || prev.eos || createEOSState();
          let stats = { ...(newState.stats || prev.stats || {}) };
          const emergencyNews = [];

          // Update aircraft to apply damage
          const updatedAircraft = [...(newState.aircraft || prev.aircraft || [])];

          for (const { flight, consequences } of emergencyLandingsToProcess) {
            const dest = flight.destination;
            const origin = flight.origin;

            // 1. FINANCIAL: Deduct total cost from cash
            cashChange -= consequences.totalCost;

            // 2. REPUTATION: Apply massive reputation hit
            repState.currentScore = Math.max(0, (repState.currentScore || 0) + consequences.reputationHit);
            repState = recordReputationEvent(repState, 'EMERGENCY_LANDING', {
              flightId: flight.id,
              severity: consequences.severity,
              cost: consequences.totalCost,
            });

            // 3. RELATIONS: Apply relations damage to both airports
            if (dest) {
              relationsState = airportShiftsLoyalty(relationsState, dest, 'PLAYER', consequences.relationsHit);
            }
            if (origin) {
              relationsState = airportShiftsLoyalty(relationsState, origin, 'PLAYER', consequences.relationsHit);
            }
            // Also hit relations at emergency landing airport (if different)
            if (consequences.nearestAirport && consequences.nearestAirport !== origin && consequences.nearestAirport !== dest) {
              relationsState = airportShiftsLoyalty(relationsState, consequences.nearestAirport, 'PLAYER', consequences.relationsHit * 0.5);
            }

            // 4. AIRCRAFT: Apply damage/degradation
            const aircraftIndex = updatedAircraft.findIndex(a => a?.id === flight.aircraftId);
            if (aircraftIndex >= 0) {
              const aircraft = updatedAircraft[aircraftIndex];
              const currentCondition = aircraft.condition || 100;
              const newCondition = Math.max(0, currentCondition - consequences.aircraftDamagePercent);
              updatedAircraft[aircraftIndex] = {
                ...aircraft,
                condition: newCondition,
                status: newCondition < 50 ? "maintenance" : aircraft.status, // Force maintenance if badly damaged
                lastEmergency: new Date().toISOString(),
              };
            }

            // 5. EOS: Record as diversion (counts against EOS qualification)
            eosState = recordDiversion(eosState, flight.id, newGameTime);

            // 6. STATS: Track emergency landings
            stats.emergencyLandings = (stats.emergencyLandings || 0) + 1;
            stats.totalFines = (stats.totalFines || 0) + consequences.breakdown.faaPenalty;

            // 7. NEWS: Add dramatic news message
            emergencyNews.push({
              type: consequences.severity === "catastrophic" ? "critical" : "warning",
              text: consequences.newsMessage,
            });

            debug(`[EMERGENCY LANDING PROCESSED] ${flight.flightNumber}: $${(consequences.totalCost / 1000000).toFixed(2)}M cost, ${consequences.reputationHit} rep, ${consequences.severity}`);
          }

          newState.relations = relationsState;
          newState.reputation = repState;
          newState.eos = eosState;
          newState.stats = stats;
          newState.aircraft = updatedAircraft;

          // Add emergency news to feed
          if (emergencyNews.length > 0) {
            newState.newsFeed = deduplicateNews(emergencyNews, newState.newsFeed || []);
          }
        }

        // ===============================================================
        // PROCESS DIVERSIONS - Apply diversion costs
        // ===============================================================
        if (diversionsToProcess.length > 0) {
          let eosState = newState.eos || prev.eos || createEOSStateFromSystem();
          let repState = newState.reputation || prev.reputation || createReputationState();
          const diversionNews = [];

          for (const { flight, alternate, reason, costs } of diversionsToProcess) {
            // 1. FINANCIAL: Deduct diversion costs
            if (costs?.totalCost) {
              cashChange -= costs.totalCost;
              debug(`[DIVERSION COST] ${flight.flightNumber}: -$${costs.totalCost.toLocaleString()}`);
            }

            // 2. REPUTATION: Apply diversion penalty (-50 points)
            repState.currentScore = Math.max(0, (repState.currentScore || 0) + (REPUTATION_POINTS.DIVERSION || -50));
            repState = recordReputationEvent(repState, 'DIVERSION', {
              flightId: flight.id,
              reason,
              divertedTo: alternate?.icao,
            });

            // 3. EOS: Record diversion (counts against EOS qualification)
            eosState = recordDiversion(eosState, flight.id, newGameTime);

            // 4. NEWS: Add diversion notification
            diversionNews.push({
              type: 'warning',
              text: `DIVERSION: ${flight.flightNumber} diverted to ${alternate?.icao || 'ALT'} - ${reason}`,
            });
          }

          newState.eos = eosState;
          newState.reputation = repState;

          if (diversionNews.length > 0) {
            newState.newsFeed = deduplicateNews(diversionNews, newState.newsFeed || []);
          }
        }

        // ===============================================================
        // PROCESS AUTO-CANCELLATIONS - Apply cancellation costs
        // ===============================================================
        if (autoCancellationsToProcess.length > 0) {
          let eosState = newState.eos || prev.eos || createEOSStateFromSystem();
          let repState = newState.reputation || prev.reputation || createReputationState();
          const cancellationNews = [];

          for (const { flight, reason, type, costs } of autoCancellationsToProcess) {
            // 1. FINANCIAL: Deduct cancellation costs (net loss = costs + lost revenue)
            if (costs?.netLoss) {
              cashChange -= costs.netLoss;
              debug(`[AUTO-CANCEL COST] ${flight.flightNumber}: -$${costs.netLoss.toLocaleString()}`);
            }

            // 2. REPUTATION: Apply cancellation penalty (-75 points)
            repState.currentScore = Math.max(0, (repState.currentScore || 0) + (REPUTATION_POINTS.CANCELLATION || -75));
            repState = recordReputationEvent(repState, 'CANCELLATION', {
              flightId: flight.id,
              reason,
              type,
            });

            // 3. EOS: Record cancellation (counts against EOS qualification)
            eosState = recordCancellation(eosState, flight.id, newGameTime);

            // 4. STATS: Track cancellations
            cancelledCount++;

            // 5. NEWS: Add cancellation notification
            cancellationNews.push({
              type: 'critical',
              text: `CANCELLED: ${flight.flightNumber} - ${reason}`,
            });
          }

          newState.eos = eosState;
          newState.reputation = repState;

          if (cancellationNews.length > 0) {
            newState.newsFeed = deduplicateNews(cancellationNews, newState.newsFeed || []);
          }
        }

        // Apply cash/reputation changes from completions and cancellations
        if (cashChange !== 0 || reputationChange !== 0 || emergencyLandingsToProcess.length > 0) {
          const newCash = Math.max(0, (newState.airline?.cash ?? prev.airline.cash) + cashChange);
          newState.airline = {
            ...(newState.airline || prev.airline),
            cash: newCash,
          };

          // ===============================================================
          // IMMEDIATE HUB TRIGGER - Check when crossing $50M (Simple/Arcade)
          // ===============================================================
          const isSimpleMode = prev.gameMode === 'simple' || prev.gameMode === 'arcade';
          const oldCash = prev.airline?.cash || 0;
          const HUB_UNLOCK_CASH = 50000000;

          if (isSimpleMode && oldCash < HUB_UNLOCK_CASH && newCash >= HUB_UNLOCK_CASH) {
            // Just crossed the $50M threshold!
            const existingHubs = prev.airline?.hubs || ['KPDX'];
            const pendingInvites = newState.pendingHubInvites || prev.pendingHubInvites || [];
            const completedAuctions = prev.airline?.completedHubAuctions || [];
            const firstHubAttempts = prev.firstHubAttempts || 0;

            // Check: only starting hub, no completed auctions, first attempt not done
            const isFirstHubOpportunity = existingHubs.length === 1 &&
                                           pendingInvites.length === 0 &&
                                           completedAuctions.length === 0 &&
                                           firstHubAttempts === 0;

            if (isFirstHubOpportunity) {
              const REGIONAL_HUBS = ['KSLC', 'KLAS', 'KSAN', 'KBNA', 'KAUS', 'KRDU', 'KCMH', 'KPIT', 'KSTL'];
              const validHubs = REGIONAL_HUBS.filter(h => !existingHubs.includes(h));
              const randomHub = validHubs[Math.floor(Math.random() * validHubs.length)];

              debug(`[GameLoop] FIRST HUB INVITE (immediate): ${randomHub} - player crossed $50M!`);
              newState.pendingHubInvites = [randomHub];
              newState.lastHubInviteDay = prev.dayNumber || 1;
              newState.firstHubAttempts = 1;
              newState.newsFeed = deduplicateNews([{
                type: "success",
                text: `HUB OPPORTUNITY: You've been invited to negotiate for a hub at ${randomHub}!`,
                timestamp: newGameTime.toISOString(),
              }], newState.newsFeed || prev.newsFeed || []);
            }
          }

          // Use ReputationEngine scale (0-15000+, no upper limit)
          newState.reputation = {
            ...(newState.reputation || prev.reputation || createReputationState()),
            currentScore: Math.max(0, (newState.reputation?.currentScore || prev.reputation?.currentScore || 0) + reputationChange),
          };

          // Merge cancellation stats with any completion stats already set
          if (cancelledCount > 0) {
            newState.stats = {
              ...(newState.stats || prev.stats || {}),
              cancelledFlights: ((newState.stats?.cancelledFlights ?? prev.stats?.cancelledFlights) || 0) + cancelledCount,
            };
          }

          if (newEvents.length > 0) {
            newState.events = [...(prev.events || []).slice(-99), ...newEvents];
          }
        }

        // ===============================================================
        // SIMPLE MODE: AUTO-CANCEL EXPIRED SCHEDULED DEPARTURES
        // Routes with scheduledDeparture > 6 hours past get auto-cancelled
        // Generous threshold - only punishes truly abandoned schedules
        // ===============================================================
        if (prev.gameMode === 'simple' || prev.gameMode === 'arcade') {
          const AUTO_CANCEL_THRESHOLD_MS = 6 * 60 * 60 * 1000; // 6 hours game time (~3 real min at 8x)
          const STALE_THRESHOLD_MS = 12 * 60 * 60 * 1000; // 12 hours - anything older is "stale" (player was away)
          const CANCELLATION_PENALTY = 25000; // $25K per missed departure (reduced)
          const CANCELLATION_REP_HIT = 5; // -5 reputation (reduced)

          const routesToCancel = [];
          const staleRoutesToClear = [];

          // Check all routes with scheduled departures
          for (const route of (prev.routes || [])) {
            if (!route.scheduledDeparture || !route.aircraftId) continue;

            const scheduledDep = new Date(route.scheduledDeparture);
            const timePastDeparture = newGameTime.getTime() - scheduledDep.getTime();

            // If way past (>2 hours), it's stale from player being away - just clear it, no penalty
            if (timePastDeparture > STALE_THRESHOLD_MS) {
              staleRoutesToClear.push(route);
              debug(`[GameLoop] STALE SCHEDULE: Route ${route.origin}-${route.destination} - clearing old schedule (${Math.round(timePastDeparture/60000)}min old)`);
            }
            // If moderately past (6-12hr), player was playing and missed it - cancel with penalty
            else if (timePastDeparture > AUTO_CANCEL_THRESHOLD_MS) {
              routesToCancel.push(route);
              debug(`[GameLoop] AUTO-CANCEL: Route ${route.origin}-${route.destination} exceeded 6hr threshold (${Math.round(timePastDeparture/60000)}min past)`);
            }
          }

          // Clear stale schedules (no penalty - player was away)
          if (staleRoutesToClear.length > 0) {
            const staleRouteIds = new Set(staleRoutesToClear.map(r => r.id));
            const currentRoutes = newState.routes || prev.routes || [];
            newState.routes = currentRoutes.map(r =>
              staleRouteIds.has(r.id)
                ? { ...r, scheduledDeparture: null, groundPhase: null, groundPhaseStartedAt: null }
                : r
            );
          }

          if (routesToCancel.length > 0) {
            // Apply penalties
            const totalPenalty = CANCELLATION_PENALTY * routesToCancel.length;
            const totalRepHit = CANCELLATION_REP_HIT * routesToCancel.length;

            newState.airline = {
              ...(newState.airline || prev.airline),
              cash: Math.max(0, (newState.airline?.cash ?? prev.airline.cash) - totalPenalty),
            };

            newState.reputation = {
              ...(newState.reputation || prev.reputation || createReputationState()),
              currentScore: Math.max(0, (newState.reputation?.currentScore || prev.reputation?.currentScore || 0) - totalRepHit),
            };

            // Update routes - clear scheduled departure, keep aircraft assigned
            const cancelledRouteIds = new Set(routesToCancel.map(r => r.id));
            const currentRoutes = newState.routes || prev.routes || [];
            newState.routes = currentRoutes.map(r =>
              cancelledRouteIds.has(r.id)
                ? {
                    ...r,
                    scheduledDeparture: null,
                    groundPhase: null,
                    groundPhaseStartedAt: null,
                    lastCancelledAt: newGameTime.toISOString(),
                  }
                : r
            );

            // Update stats
            newState.stats = {
              ...(newState.stats || prev.stats || {}),
              cancelledFlights: ((newState.stats?.cancelledFlights ?? prev.stats?.cancelledFlights) || 0) + routesToCancel.length,
              dailyCancellations: ((newState.stats?.dailyCancellations ?? prev.stats?.dailyCancellations) || 0) + routesToCancel.length,
            };

            // Add news
            const cancellationNews = routesToCancel.map(route => ({
              type: 'critical',
              text: `CANCELLED: ${route.origin}-${route.destination} - Missed scheduled departure (-$25K)`,
              timestamp: newGameTime.toISOString(),
            }));
            newState.newsFeed = deduplicateNews(cancellationNews, newState.newsFeed || prev.newsFeed || []);
          }
        }

        // ===============================================================
        // SLOT STATUS UPDATES - Track departures and completions
        // ===============================================================
        if (prev.gameMode === 'simulation' && (slotDeparturesToProcess.length > 0 || slotCompletionsToProcess.length > 0)) {
          let updatedSlots = { ...(newState.slots || prev.slots) };

          // Update slots to DEPARTED when flights launch
          for (const { slotId, airportCode } of slotDeparturesToProcess) {
            const airportSlots = updatedSlots[airportCode];
            if (airportSlots) {
              updatedSlots[airportCode] = airportSlots.map(slot =>
                slot.slotId === slotId
                  ? { ...slot, status: SLOT_STATUS.DEPARTED }
                  : slot
              );
              debug(`[GameLoop] Slot ${slotId} status updated to DEPARTED`);
            }
          }

          // Update slots to COMPLETED when flights finish
          for (const { slotId, airportCode } of slotCompletionsToProcess) {
            const airportSlots = updatedSlots[airportCode];
            if (airportSlots) {
              updatedSlots[airportCode] = airportSlots.map(slot =>
                slot.slotId === slotId
                  ? { ...slot, status: SLOT_STATUS.COMPLETED }
                  : slot
              );
              debug(`[GameLoop] Slot ${slotId} status updated to COMPLETED`);
            }
          }

          newState.slots = updatedSlots;
        }

        // ===============================================================
        // SLOT WARNING SYSTEM (Simulation Mode)
        // Process slot warnings and update status
        // ===============================================================
        if (prev.gameMode === 'simulation' && prev.slots) {
          const slotWarningResult = processSlotWarnings(
            prev.slots,
            prev.flights || [],
            newGameTime
          );

          if (slotWarningResult.updates.length > 0 || slotWarningResult.warnings.length > 0) {
            // Apply slot status updates
            let updatedSlots = { ...(newState.slots || prev.slots) };
            for (const update of slotWarningResult.updates) {
              const airportSlots = updatedSlots[update.airportCode];
              if (airportSlots) {
                updatedSlots[update.airportCode] = airportSlots.map(slot =>
                  slot.slotId === update.slotId
                    ? { ...slot, status: update.status }
                    : slot
                );
              }
            }
            newState.slots = updatedSlots;

            // Play warning sounds
            for (const warning of slotWarningResult.warnings) {
              if (warning.type === 'warning') {
                soundManager.play(SOUNDS.WARNING);
              } else if (warning.type === 'urgent') {
                soundManager.play(SOUNDS.ALERT_CRITICAL);
              }
            }

            // Add warning news
            const slotNews = slotWarningResult.warnings.slice(0, 2).map(w => ({
              type: w.type === 'urgent' || w.type === 'delayed' ? 'critical' : 'warning',
              text: `SLOT ${w.slotId}: ${w.message}`,
            }));
            if (slotNews.length > 0) {
              newState.newsFeed = deduplicateNews(slotNews, newState.newsFeed || prev.newsFeed || []);
            }
          }

          // ===============================================================
          // SLOT DELAY PENALTIES (Simulation Mode)
          // Apply ongoing costs for delayed slots
          // ===============================================================
          let delayPenaltiesTotal = 0;
          let delayRepHit = 0;
          const delayedSlots = [];

          for (const [airportCode, airportSlots] of Object.entries(newState.slots || prev.slots)) {
            if (!Array.isArray(airportSlots)) continue;

            for (const slot of airportSlots) {
              // Skip slots without departure times (old format)
              if (!slot.departureTime) continue;

              if (slot.status === SLOT_STATUS.DELAYED) {
                const delayMinutes = calculateSlotDelay(slot, newGameTime);
                const penalties = calculateDelayPenalties(delayMinutes, 76); // Assume ~76 pax

                // Apply per-tick portion of penalties (scaled by tick rate)
                // Uses clockDeltaMs (fixed rate) since penalties accrue with game time, not flight speed
                const tickMinutes = (clockDeltaMs / 60000); // game minutes per tick
                const penaltyThisTick = (penalties.gateFees + penalties.crewOvertime) * (tickMinutes / 60);

                delayPenaltiesTotal += penaltyThisTick;
                delayRepHit += penalties.reputationHit * (tickMinutes / 60);

                delayedSlots.push({
                  slotId: slot.slotId,
                  airport: airportCode,
                  delayMinutes,
                  penaltiesThisTick: penaltyThisTick,
                });
              }
            }
          }

          // Apply delay penalties to cash
          if (delayPenaltiesTotal > 0) {
            newState.airline = {
              ...(newState.airline || prev.airline),
              cash: Math.max(0, (newState.airline?.cash ?? prev.airline.cash) - delayPenaltiesTotal),
            };
          }

          // Apply reputation hit from delays (only significant hits)
          if (Math.abs(delayRepHit) >= 0.1) {
            newState.reputation = {
              ...(newState.reputation || prev.reputation || createReputationState()),
              currentScore: Math.max(0, (newState.reputation?.currentScore || prev.reputation?.currentScore || 0) - delayRepHit),
            };
          }
        }

        // ===============================================================
        // SIMPLE/ARCADE MODE: ROUTE LANDED → READY TRANSITION
        // After the landing beat (~3 real sec / ~7 game min), transition to ready
        // ===============================================================
        if (prev.gameMode === 'simple' || prev.gameMode === 'arcade') {
          const LANDING_BEAT_MINUTES = 7; // ~3 real seconds at 144x clock
          const currentRoutes = newState.routes || prev.routes || [];
          let routesUpdated = false;

          const updatedRoutes = currentRoutes.map(route => {
            if (route.status === 'landed' && route.landedAt) {
              const landedTime = new Date(route.landedAt);
              const gameMinutesSinceLanding = (newGameTime.getTime() - landedTime.getTime()) / 60000;

              if (gameMinutesSinceLanding >= LANDING_BEAT_MINUTES) {
                routesUpdated = true;
                debug(`[GameLoop] Route ${route.id} transitioning from landed to ready at ${route.currentLegOrigin}`);
                return {
                  ...route,
                  status: 'ready',
                  landedAt: null, // Clear landing timestamp
                };
              }
            }
            return route;
          });

          if (routesUpdated) {
            newState.routes = updatedRoutes;
          }
        }

        // ===============================================================
        // SIMPLE MODE: HUB CAPACITY CHECK
        // Trigger hub offer when a hub reaches 30 aircraft
        // ===============================================================
        if (prev.gameMode === 'simple' || prev.gameMode === 'arcade') {
          const HUB_CAPACITY = 30;
          const playerHubs = prev.airline?.hubs || ['KPDX'];
          const aircraft = newState.aircraft || prev.aircraft || [];
          const pendingInvites = newState.pendingHubInvites || prev.pendingHubInvites || [];
          const completedAuctions = prev.airline?.completedHubAuctions || [];
          const exclusiveHubsAwarded = newState.exclusiveHubsAwarded ?? prev.exclusiveHubsAwarded ?? 0;

          // Count aircraft per hub (location OR homeBase - matches purchase check)
          const HUB_OFFER_THRESHOLD = 25; // Trigger hub offer at 25, before hitting 30 capacity
          for (const hub of playerHubs) {
            const aircraftAtHub = aircraft.filter(a => a.location === hub || a.homeBase === hub).length;

            // Check if this hub reached the threshold (25) - give player time to expand before hitting cap (30)
            if (aircraftAtHub >= HUB_OFFER_THRESHOLD) {
              const alreadyNotified = prev.hubCapacityNotified?.[hub];
              const alreadyHasExtraHub = playerHubs.length > 1;

              // Only notify once per hub reaching threshold
              if (!alreadyNotified) {
                debug(`[GameLoop] HUB EXPANSION: ${hub} has ${aircraftAtHub} aircraft (threshold: ${HUB_OFFER_THRESHOLD}, cap: ${HUB_CAPACITY}) - triggering expansion offer`);

                // Find nearest eligible hub to offer
                const hubAirport = AIRPORTS[hub] || FULL_AIRPORTS[hub];
                const nearbyHubs = ['KSEA', 'KSLC', 'KLAS', 'KPHX', 'KSAN', 'KSFO', 'KDEN', 'KLAX'];

                // Find a hub not already owned or invited
                let offerHub = null;
                for (const candidate of nearbyHubs) {
                  if (!playerHubs.includes(candidate) &&
                      !pendingInvites.includes(candidate) &&
                      !completedAuctions.includes(candidate)) {
                    offerHub = candidate;
                    break;
                  }
                }

                if (offerHub) {
                  // Trigger capacity-based hub offer
                  if (!newState.pendingHubInvites) {
                    newState.pendingHubInvites = [...pendingInvites];
                  }
                  if (!newState.pendingHubInvites.includes(offerHub)) {
                    newState.pendingHubInvites.push(offerHub);
                    debug(`[GameLoop] Capacity-triggered hub offer: ${offerHub}`);
                  }
                }

                // Mark this hub as notified for capacity
                newState.hubCapacityNotified = {
                  ...(prev.hubCapacityNotified || {}),
                  [hub]: true,
                };
              }
            }
          }
        }

        // ===============================================================
        // SCHEDULED FLIGHTS DISPATCH
        // Check if any scheduled flights should fire based on game time
        // ===============================================================
        const schedules = newState.scheduledFlights || prev.scheduledFlights || [];
        if (schedules.length > 0) {
          const oldH = oldGameTime.getUTCHours();
          const oldM = oldGameTime.getUTCMinutes();
          const newH = newGameTime.getUTCHours();
          const newM = newGameTime.getUTCMinutes();
          const oldTotalMin = oldH * 60 + oldM;
          const newTotalMin = newH * 60 + newM;
          const currentDayKey = newGameTime.toISOString().slice(0, 10);

          let updatedSchedules = [...schedules];
          let scheduledFlightsToCreate = [];

          for (let i = 0; i < updatedSchedules.length; i++) {
            const sched = updatedSchedules[i];
            if (!sched.enabled) continue;

            // Parse departure time
            const [hStr, mStr] = sched.departureTime.split(':');
            const schedMin = parseInt(hStr) * 60 + parseInt(mStr);

            // Check if schedule time falls between old and new time
            // Handle midnight wraparound: if newTotalMin < oldTotalMin, day rolled over
            let shouldFire = false;
            if (newTotalMin >= oldTotalMin) {
              // Normal case: no midnight wrap
              shouldFire = schedMin > oldTotalMin && schedMin <= newTotalMin;
            } else {
              // Midnight wrap: fire if after old OR before/at new
              shouldFire = schedMin > oldTotalMin || schedMin <= newTotalMin;
            }

            // Prevent double-fire for recurring schedules
            if (shouldFire && sched.recurring && sched.lastDispatchedDay === currentDayKey) {
              shouldFire = false;
            }

            if (!shouldFire) continue;

            // Find the route
            const routes = newState.routes || prev.routes || [];
            const route = routes.find(r => r.id === sched.routeId);
            if (!route) continue;

            // Find aircraft
            const allAircraft = newState.aircraft || prev.aircraft || [];
            const flights = newState.flights || prev.flights || [];
            const inFlightIds = new Set(flights.filter(f => f.status !== 'completed' && f.status !== 'cancelled').map(f => f.aircraftId));

            let ac = null;
            if (sched.aircraftId === 'next-available') {
              // Find first ready aircraft not in flight
              ac = allAircraft.find(a => !inFlightIds.has(a.id) && a.status !== 'in_flight');
            } else {
              ac = allAircraft.find(a => a.id === sched.aircraftId);
              if (ac && (inFlightIds.has(ac.id) || ac.status === 'in_flight')) {
                ac = null; // Aircraft busy
              }
            }

            if (!ac) continue; // No aircraft available, skip this tick

            const legOrigin = route.currentLegOrigin || route.origin;
            const legDest = route.currentLegDestination || route.destination;
            const distance = calculateDistance(legOrigin, legDest);
            if (!distance || distance <= 0) continue;

            const acSpec = AIRCRAFT_DATABASE[ac.type || ac.model] || {};
            const aircraftRange = ac.range || acSpec.range || 2000;

            // Skip if distance exceeds aircraft range — don't dispatch suicidal flights
            if (distance > aircraftRange) {
              debug(`[GameLoop] SCHEDULE SKIP: ${legOrigin}→${legDest} (${Math.round(distance)}nm) exceeds range (${aircraftRange}nm)`);
              continue;
            }

            const flightTime = calculateFlightTime(distance, acSpec.cruiseSpeed || 450);
            const earnings = calculateEarnings({
              origin: legOrigin,
              destination: legDest,
              aircraftId: ac.id,
              aircraftType: ac.type || ac.model,
            }, prev);

            scheduledFlightsToCreate.push({
              origin: legOrigin,
              destination: legDest,
              aircraftId: ac.id,
              aircraftType: ac.type || ac.model,
              routeId: route.id,
              immediateDepart: true,
              scheduledDeparture: newGameTime.toISOString(),
              scheduledArrival: new Date(newGameTime.getTime() + flightTime * 60 * 60 * 1000).toISOString(),
              crew: [],
              passengers: earnings.passengers || 100,
              estimatedRevenue: earnings.revenue || 0,
              estimatedCost: earnings.costs || 0,
              preCalculatedEarnings: earnings,
              rangeExceeded: false,
              totalDelayMinutes: 0,
            });

            // Update schedule
            if (sched.recurring) {
              updatedSchedules[i] = { ...sched, lastDispatchedDay: currentDayKey };
            } else {
              updatedSchedules[i] = { ...sched, enabled: false };
            }
          }

          // Remove disabled one-time schedules
          updatedSchedules = updatedSchedules.filter(s => s.enabled || s.recurring);
          newState.scheduledFlights = updatedSchedules;

          // Create the flights (they'll be picked up by the flight processing on next tick)
          if (scheduledFlightsToCreate.length > 0) {
            const currentFlights = newState.flights || prev.flights || [];
            const currentAircraft = newState.aircraft || prev.aircraft || [];

            for (const flightData of scheduledFlightsToCreate) {
              const flightId = `FLT-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
              const flightNumber = `${prev.airline?.code || 'XX'}${Math.floor(Math.random() * 9000) + 1000}`;

              currentFlights.push({
                id: flightId,
                flightNumber,
                ...flightData,
                status: 'in_flight',
                phase: 'cruise',
                progress: 0,
                distanceFraction: 0,
                hasLaunched: true,
                launchedAt: newGameTime.toISOString(),
                gameLoopProcessed: false,
              });

              // Mark aircraft as in flight
              const acIdx = currentAircraft.findIndex(a => a.id === flightData.aircraftId);
              if (acIdx >= 0) {
                currentAircraft[acIdx] = {
                  ...currentAircraft[acIdx],
                  status: 'in_flight',
                  currentFlightId: flightId,
                };
              }

              // Update route
              const routeArr = newState.routes || prev.routes || [];
              const rIdx = routeArr.findIndex(r => r.id === flightData.routeId);
              if (rIdx >= 0) {
                routeArr[rIdx] = {
                  ...routeArr[rIdx],
                  lastFlightId: flightId,
                  status: 'in_flight',
                };
                newState.routes = routeArr;
              }

              debug(`[GameLoop] SCHEDULED DISPATCH: ${flightNumber} ${flightData.origin}→${flightData.destination}`);
            }

            newState.flights = currentFlights;
            newState.aircraft = currentAircraft;

            // Increment dailyDepartures so scheduled flights count toward EOS 20+ flights/day
            const prevStats = newState.stats || prev.stats || {};
            newState.stats = {
              ...prevStats,
              dailyDepartures: (prevStats.dailyDepartures || 0) + scheduledFlightsToCreate.length,
            };
          }
        }

        // ===============================================================
        // AUTO-RETURN: Create return schedule 30 min after flight completion
        // ===============================================================
        if (flightCompletions.length > 0) {
          const returnSchedules = newState.scheduledFlights || prev.scheduledFlights || [];
          const returnTime = new Date(newGameTime.getTime() + 30 * 60 * 1000);
          const returnH = returnTime.getUTCHours().toString().padStart(2, '0');
          const returnM = returnTime.getUTCMinutes().toString().padStart(2, '0');

          for (const flight of flightCompletions) {
            if (!flight.routeId || !flight.aircraftId) continue;

            // Skip emergency-landed / diverted flights — aircraft isn't at expected location
            if (flight.status === 'emergency_landed' || flight.phase === 'emergency_landed' ||
                flight.rangeExceeded || flight.diverted) continue;

            // Check if there's already a schedule for this route (don't double-schedule)
            const hasExisting = returnSchedules.some(s =>
              s.routeId === flight.routeId && s.enabled && !s.autoReturn
            );
            if (hasExisting) continue;

            returnSchedules.push({
              id: `sched-ret-${Date.now()}-${Math.random().toString(36).slice(2, 4)}`,
              routeId: flight.routeId,
              aircraftId: flight.aircraftId,
              departureTime: `${returnH}:${returnM}`,
              recurring: false,
              enabled: true,
              autoReturn: true,
            });

            debug(`[GameLoop] AUTO-RETURN scheduled: ${flight.flightNumber} return at ${returnH}:${returnM}`);
          }

          newState.scheduledFlights = returnSchedules;
        }

        return newState;
      });
    }, 100); // Tick every 100ms for smooth clock

    return () => clearInterval(interval);
  }, [worldState?.isPaused, worldState?.gameSpeed, setWorldState, completeFlight]);

  // Wings milestone checking - runs every 5 seconds
  useEffect(() => {
    if (!worldState || worldState.isPaused) return;

    const milestoneInterval = setInterval(() => {
      const awarded = processMilestones(worldState, setWorldState);
      if (awarded.length > 0) {
        debug('%c WINGS MILESTONE ACHIEVED!', 'color: #a78bfa; font-weight: bold; font-size: 14px;');
        for (const m of awarded) {
          debug(`%c  +${m.wings} Wings: ${m.label}`, 'color: #a78bfa; font-weight: bold;');
        }
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(milestoneInterval);
  }, [worldState, setWorldState]);

  // CRASH DEBUG: Periodic state snapshot for crash logging
  // Saves game state every 10 seconds so we know the state before a crash
  useEffect(() => {
    if (!worldState) return;

    const snapshotInterval = setInterval(() => {
      // Save state snapshot to global function (defined in ErrorBoundary)
      if (typeof window.__saveStateSnapshot === 'function') {
        window.__saveStateSnapshot(worldState);
      }
    }, 10000); // Every 10 seconds

    // Also save immediately on mount
    if (typeof window.__saveStateSnapshot === 'function') {
      window.__saveStateSnapshot(worldState);
    }

    return () => clearInterval(snapshotInterval);
  }, [worldState]);

  return null; // This is a logic-only component
}

function calculateOverallProgress(flight) {
  // If distanceFraction is set (new distance-based system), use it directly
  if (typeof flight.distanceFraction === 'number') {
    return flight.distanceFraction;
  }

  // Fallback for ground ops phases (before takeoff)
  const phaseWeights = {
    boarding: 0,
    pushback: 0,
    taxi_out: 0.02,
    takeoff: 0.03,
    climb: 0.05,
    cruise: 0.15,
    descent: 0.85,
    approach: 0.92,
    landing: 0.96,
    taxi_in: 0.98,
    arrived: 1,
    completed: 1,
  };

  const baseProgress = phaseWeights[flight.phase] || 0;
  const nextPhase = FLIGHT_PHASES[flight.phase]?.next;
  const nextWeight = phaseWeights[nextPhase] || baseProgress;
  const phaseProgress = flight.progress || 0;

  return baseProgress + (nextWeight - baseProgress) * phaseProgress;
}

// ============================================================================
// HELPER HOOKS
// ============================================================================

export function useAvailableAircraft() {
  const { worldState } = useWorldState();
  if (!worldState?.aircraft) return [];
  return worldState.aircraft.filter(
    (a) => a.status === "available" || a.status === "turnaround"
  );
}

export function useAvailableCrew(aircraftType) {
  const { worldState } = useWorldState();
  if (!worldState?.crew) return { captains: [], firstOfficers: [], flightAttendants: [] };
  return {
    captains: worldState.crew.filter(
      (c) => c.role === "captain" && c.status === "available" && (c.rating === aircraftType || c.rating === "all")
    ),
    firstOfficers: worldState.crew.filter(
      (c) => (c.role === "firstOfficer" || c.role === "first_officer") && c.status === "available" && (c.rating === aircraftType || c.rating === "all")
    ),
    flightAttendants: worldState.crew.filter(
      (c) => (c.role === "flightAttendant" || c.role === "flight_attendant") && c.status === "available"
    ),
  };
}

export function useActiveFlights() {
  const { worldState } = useWorldState();
  if (!worldState?.flights) return [];

  // Return flights that are actively in progress (not done)
  const AIRBORNE_PHASES = ["takeoff", "climb", "cruise", "descent", "approach", "landing", "taxi_in"];
  // Note: "arrived" removed - those flights are done

  return worldState.flights.filter((f) => {
    if (!f) return false;

    // Exclude completed/cancelled/emergency flights
    if (f.phase === "completed" || f.status === "completed") return false;
    if (f.status === "cancelled" || f.phase === "cancelled") return false;
    if (f.status === "emergency_landed" || f.phase === "emergency_landed") return false;

    // Exclude flights that have arrived or are awaiting visual completion
    if (f.phase === "arrived" || f.status === "arrived") return false;
    if (f.awaitingVisualArrival === true) return false;

    // NOTE: Don't filter by progress here - the map needs to see flights until 100%
    // The Routes page handles its own "effectively done" logic

    // Include if launched and still in progress
    if (f.hasLaunched) return true;

    // Include if in an airborne phase (legacy support)
    if (AIRBORNE_PHASES.includes(f.phase)) return true;

    return false;
  });
}

// Hook to get recently cancelled flights (within last 6 game hours)
export function useRecentCancellations() {
  const { worldState } = useWorldState();
  if (!worldState?.flights) return [];

  const gameTime = worldState.gameTime ? new Date(worldState.gameTime).getTime() : Date.now();
  const sixHoursAgo = gameTime - (6 * 60 * 60 * 1000); // 6 game hours

  return worldState.flights.filter((f) => {
    if (!f) return false;
    if (f.status !== "cancelled" && f.phase !== "cancelled") return false;

    // Check if cancelled recently (within 6 game hours)
    const cancelledAt = f.cancelledAt ? new Date(f.cancelledAt).getTime() : 0;
    return cancelledAt > sixHoursAgo;
  });
}

// ============================================================================
// NEW: MACRO-AWARE HOOKS
// ============================================================================

/**
 * Get current world status from macro engine
 */
export function useWorldStatus() {
  const { worldState, getWorldStatusInfo } = useWorldState();
  if (!worldState?.macro) return null;
  return getWorldStatusInfo ? getWorldStatusInfo() : null;
}

/**
 * Get route profitability estimate with macro influence
 * TODO: Wire up to integrated macro system when ready
 */
export function useRouteProfitability(origin, destination, aircraftType) {
  // Placeholder - returns null until macro system is integrated
  return null;
}

/**
 * Get current fuel price indicator
 */
export function useFuelPrice() {
  const { worldState } = useWorldState();
  if (!worldState?.macro) return { multiplier: 1.0, level: 'STABLE' };

  const multiplier = worldState.macro.jetFuelPriceMultiplier || 1.0;

  let level = 'STABLE';
  if (multiplier < 0.85) level = 'LOW';
  else if (multiplier < 1.0) level = 'STABLE';
  else if (multiplier < 1.2) level = 'ELEVATED';
  else if (multiplier < 1.5) level = 'HIGH';
  else level = 'CRITICAL';

  return { multiplier, level };
}

/**
 * Get news feed from macro engine
 */
export function useNewsFeed() {
  const { worldState } = useWorldState();
  return worldState?.newsFeed || [];
}

// Export system functions for external use
export {
  getReputationTier,
  TIER_THRESHOLDS,
  TIER_THRESHOLDS as REPUTATION_TIERS, // Backwards compatibility alias
  REPUTATION_POINTS,
  CREW_LIMITS,
  MAINTENANCE_INTERVALS,
  checkMaintenanceDue,
  performMaintenance,
  purchaseA380,
  createEOSState,
  createCrewDutyState,
  createMaintenanceState,
  createReputationState,
  // Re-export EOS functions from EOSSystem.js
  processA380Purchase,
  getEOSStatus,
  recordCancellation,
  recordDiversion,
  EOS_REQUIREMENTS,
  ROLL_PROBABILITIES,
  RARE_AIRCRAFT,
};
